import { id, InstaQLEntity, lookup } from "@instantdb/react";
import { parsePhoneNumber } from "libphonenumber-js/max";
import { coerce } from "@uri/gamla";
import { apiClient, httpCommunication } from "@uri/typed-api";
import {
  ErrorBoundary,
  LocationProvider,
  Route,
  Router,
  useLocation,
} from "preact-iso";
import hydrate from "preact-iso/hydrate";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { toast, Toaster } from "react-hot-toast";
import {
  lineServerApiDefinition,
  telegramApiDefinition,
  userApiDefinition,
} from "../../client/src/apiTyping.ts";
import { apiBaseUrl } from "../../client/src/main.ts";
import {
  dailyMessageCap,
  monthlyPaymentPerLine,
  pbxmeNumberPriceUsd,
  supportEmail,
  trialDurationMs,
} from "../../constants.ts";
import { type DashboardTab, tabOrder } from "../../dashboardTabs.ts";
import schema, { whatsappAccountStatus } from "../../instant.schema.ts";
import { ApiDocs } from "./apiDocs.tsx";
import { auth, queryOnce, transact, tx, useAuth, useQuery } from "./db.ts";
import {
  downtimeBinsLast24h,
  downtimeMsLast24h,
  formatDowntime,
  uptimePercentageLast24h,
} from "./downtime.ts";
import {
  LegalPage,
  PrivacyPage,
  RefundPolicyPage,
  TermsPage,
} from "./legal.tsx";
import { Login } from "./login.tsx";
import {
  alertDialog,
  Button,
  confirmDialog,
  DialogHost,
  ErrorMessage,
  formatDate,
  formatDateOnly,
  formatPhone,
  LoadingIndication,
  Modal,
  PairingCodeWithTimer,
  promptDialog,
  QrWithTimer,
  SecretToken,
  selectDialog,
} from "./uiComponents.tsx";
import { PhoneNumbersView } from "./phoneNumbers.tsx";
import {
  AlertTriangleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  HelpCircleIcon,
  ImageIcon,
  InfoIcon,
  KeyIcon,
  ListIcon,
  LockIcon,
  MailIcon,
  MessageSquareIcon,
  PencilIcon,
  PhoneIcon,
  PlayIcon,
  PlusIcon,
  QrCodeIcon,
  RefreshCwIcon,
  SmartphoneIcon,
  SparklesIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
  WifiIcon,
  ZapIcon,
} from "./icons.tsx";
import { UserContext, useUser } from "./userContext.tsx";
import { identifyPosthog } from "./analytics.ts";
import {
  trackConnectedNumberConversion,
  trackPurchaseConversion,
} from "./googleAds.ts";

const wrapperStyle = {
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  flexGrow: 1,
  width: "100%",
  height: "100vh", // Ensure wrapper fills viewport
  minHeight: 0, // Prevent flex children from growing unexpectedly
};

// Action row for account controls; wraps on narrow screens
const accountActionRowStyle = {
  display: "flex",
  gap: 8,
  flexDirection: "row",
  flexWrap: "wrap",
  marginTop: 10,
  paddingTop: 10,
  borderTop: "1px solid var(--border)",
} as const;

const advancedSectionStyle = {
  fontSize: 12,
  opacity: 0.85,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  paddingLeft: 12,
  marginTop: 2,
} as const;

const thresholdRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
} as const;

const adminsRowStyle = {
  fontSize: 12,
  opacity: 0.85,
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap" as const,
  paddingLeft: 12,
  marginTop: 2,
} as const;

const paymentOptionsStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 12,
  marginTop: 12,
} as const;

const paymentOptionStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 8,
  padding: 16,
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--surface)",
  minHeight: 170,
  textAlign: "left",
} as const;

const paymentOptionTitleStyle = {
  fontSize: 17,
  fontWeight: 800,
  lineHeight: 1.2,
} as const;

const paymentOptionBodyStyle = {
  fontSize: 13,
  lineHeight: 1.45,
  opacity: 0.78,
  flexGrow: 1,
} as const;

// Connection help section styles and content
const connectHelpStyle = {
  marginTop: 12,
  padding: 12,
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  background: "var(--surface-alt)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
} as const;

const adminChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "var(--surface-alt)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: "2px 8px",
} as const;

const accountCardStyle = {
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: 12,
  background: "var(--surface-alt)",
} as const;

// Same card, but as a self-contained <li> (parent <ul> is unstyled here).
const accountListCardStyle = {
  ...accountCardStyle,
  padding: 14,
  listStyle: "none",
} as const;

const collapsibleSectionStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: 6,
} as const;

const collapsibleHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  userSelect: "none",
  fontSize: 13,
  fontWeight: 600,
} as const;

const collapsibleChevronStyle = {
  display: "inline-flex",
  alignItems: "center",
  opacity: 0.6,
} as const;

const CollapsibleSection = (
  { title, children }: {
    title: preact.ComponentChildren;
    children: preact.ComponentChildren;
  },
) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={collapsibleSectionStyle}>
      <div style={collapsibleHeaderStyle} onClick={() => setOpen((v) => !v)}>
        <span style={collapsibleChevronStyle}>
          {open
            ? <ChevronDownIcon size={12} />
            : <ChevronRightIcon size={12} />}
        </span>
        <span>{title}</span>
      </div>
      {open && children}
    </div>
  );
};

export const ConnectHelp = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={connectHelpStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <HelpCircleIcon size={16} /> Connection Help
        </div>
        <Button
          className="secondary"
          style={{ fontSize: 12, padding: "4px 10px" }}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? "Hide" : "Show"}
        </Button>
      </div>
      {isOpen && (
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <li style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <SmartphoneIcon size={16} style={{ marginTop: 2 }} />
            <span>
              WhatsApp supports up to four linked devices. In WhatsApp on your
              phone: Settings → Linked Devices. If you're at the limit,
              disconnect one and try again.
            </span>
          </li>
          <li style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <QrCodeIcon size={16} style={{ marginTop: 2 }} />
            <span>
              QR codes expire quickly. If the QR disappears or fails, refresh
              and press <strong>Generate QR Code</strong> again.
            </span>
          </li>
          <li style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <MailIcon size={16} style={{ marginTop: 2 }} />
            <span>
              If you can't scan a QR, or you're connecting on the same device,
              use <strong>Generate Login Code</strong>.
            </span>
          </li>
          <li style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <RefreshCwIcon size={16} style={{ marginTop: 2 }} />
            <span>
              It may take a few attempts. If a try fails, trigger{" "}
              <strong>Generate QR Code</strong> or{" "}
              <strong>Generate Login Code</strong> again.
            </span>
          </li>
          <li style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <WifiIcon size={16} style={{ marginTop: 2 }} />
            <span>
              Make sure WhatsApp on your phone is online, the app is up to date,
              and battery saver/background restrictions aren’t blocking it.
            </span>
          </li>
          <li style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <SparklesIcon size={16} style={{ marginTop: 2 }} />
            <span>
              If you get stuck, use <strong>Reset</strong>{" "}
              to clear credentials, then try connecting again.
            </span>
          </li>
          <li
            style={{
              marginTop: 8,
              borderTop: "1px solid var(--border)",
              paddingTop: 8,
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            <MailIcon size={16} style={{ marginTop: 2 }} />
            <span>
              Need help with billing or account limits? Contact{" "}
              <a
                href={`mailto:${supportEmail}`}
                style={{ color: "var(--primary)" }}
              >
                {supportEmail}
              </a>
            </span>
          </li>
        </ul>
      )}
    </div>
  );
};

const Header = () => {
  const { user } = useAuth();

  return (
    <div
      style={{
        padding: "10px 20px",
        backgroundColor: "#282c34",
        color: "white",
        display: "flex",
        alignItems: "center",
        gap: "24px",
        flexWrap: "wrap",
      }}
    >
      <a href="/" style={{ color: "white", textDecoration: "none" }}>
        <h1 style={{ margin: 0 }}>Supergreen</h1>
      </a>
      <nav
        style={{
          display: "flex",
          gap: "16px",
          fontSize: "14px",
          flex: 1,
        }}
      >
        <a
          href="/app"
          style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
          onMouseOver={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
        >
          Dashboard
        </a>
        <a
          href="/docs"
          style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
          onMouseOver={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
        >
          API Docs
        </a>
        <a
          href="/pricing"
          style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
          onMouseOver={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.opacity = "0.9";
          }}
        >
          Pricing
        </a>
        {user?.email === "uri.valevski@gmail.com" && (
          <a
            href="/admin"
            style={{ color: "white", textDecoration: "none", opacity: 0.9 }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = "0.9";
            }}
          >
            Admin Panel
          </a>
        )}
      </nav>
      {user?.email && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontSize: 14,
            marginLeft: "auto",
          }}
        >
          <span style={{ opacity: 0.9 }}>{user.email}</span>
          <Button
            className="secondary"
            style={{
              fontSize: 12,
              padding: "5px 10px",
              borderColor: "rgba(255,255,255,0.35)",
              color: "white",
            }}
            onClick={() => {
              toast
                .promise(auth.signOut(), {
                  loading: "Signing out...",
                  success: "Signed out",
                  error: "Failed to sign out",
                })
                .then(() => {
                  globalThis.location.href = "/";
                });
            }}
          >
            Sign out
          </Button>
        </div>
      )}
    </div>
  );
};

const footerLinkStyle = {
  color: "var(--text-muted)",
  textDecoration: "none",
  fontSize: "13px",
};

const Footer = () => (
  <footer
    style={{
      padding: "20px",
      textAlign: "center",
      display: "flex",
      justifyContent: "center",
      gap: "16px",
      flexWrap: "wrap",
      borderTop: "1px solid var(--border)",
      marginTop: "auto",
    }}
  >
    <a href="/terms" style={footerLinkStyle}>Terms</a>
    <a href="/privacy" style={footerLinkStyle}>Privacy</a>
    <a href="/refund-policy" style={footerLinkStyle}>Refunds</a>
  </footer>
);

const client = apiClient(
  httpCommunication(apiBaseUrl),
  lineServerApiDefinition,
);
const telegramClient = apiClient(
  httpCommunication(apiBaseUrl),
  telegramApiDefinition,
);
const userClient = apiClient(httpCommunication(apiBaseUrl), userApiDefinition);

const normalizeWaId = (input: string) => {
  const s = input.trim();
  if (!s) return s;
  // If user already provided a full chat id (contains @), keep as-is
  if (s.includes("@")) return s;
  const digits = s.replace(/\D/g, "");
  return `${digits}@c.us`;
};

const getToken = async (phoneNumber: string) => {
  const { data } = await queryOnce({
    whatsappAccounts: { $: { where: { phoneNumber } } },
  });
  return data?.whatsappAccounts?.[0]?.secretToken || "";
};

const getTelegramToken = async (phoneNumber: string) => {
  const { data } = await queryOnce({
    telegramAccounts: { $: { where: { phoneNumber } } },
  });
  return data?.telegramAccounts?.[0]?.secretToken || "";
};

const addAdmin = async (
  accountId: string,
  existingEmails: ReadonlyArray<string>,
) => {
  const raw = await promptDialog("Enter admin email:", {
    title: "Add admin",
    placeholder: "name@example.com",
    inputMode: "email",
  });
  if (!raw) return;
  const email = raw.trim();
  if (!email) return;
  if (existingEmails.includes(email)) {
    toast.error("Admin already added");
    return;
  }
  const promise = transact(
    tx.whatsappAccounts[accountId].link({ admins: lookup("email", email) }),
  );
  await toast.promise(promise, {
    loading: "Adding admin...",
    success: "Admin added",
    // deno-lint-ignore no-explicit-any
    error: (e: any) => {
      if (
        e.body?.message?.includes("does not exist") ||
        e.message?.includes("does not exist")
      ) {
        return `User ${email} must log in at least once first.`;
      }
      return e?.message || "Failed to add admin";
    },
  });
};

const removeAdmin = async (
  accountId: string,
  adminId: string,
  adminEmail?: string,
) => {
  const ok = await confirmDialog(
    `Remove ${adminEmail ?? "admin"} from this WhatsApp account?`,
    { title: "Remove admin", okLabel: "Remove", danger: true },
  );
  if (!ok) return;
  const promise = transact(
    tx.whatsappAccounts[accountId].unlink({ admins: adminId }),
  );
  await toast.promise(promise, {
    loading: "Removing admin...",
    success: "Admin removed",
    error: (e) => e?.message || "Failed to remove admin",
  });
};

const formatToastError = (e: unknown, fallback: string) => {
  const msg = (e as { message?: string })?.message;
  if (!msg) return fallback;
  if (
    msg.includes("Invariant Violation") ||
    msg.includes("getChatTable") ||
    msg.includes("getStorage") ||
    msg.includes("Minified invariant")
  ) {
    return "WhatsApp is not connected or session is not ready. Please scan the QR code.";
  }
  const firstLine = msg.split("\n")[0].trim();
  return firstLine.startsWith("Failed:") ? firstLine : `Failed: ${firstLine}`;
};

const sendTest = async (fromNumber: string, userWhatsapp?: string) => {
  const raw = await promptDialog(
    "Enter destination phone (digits or full chat id):",
    {
      title: "Send test message",
      defaultValue: userWhatsapp || "",
      placeholder: "1...",
      inputMode: "tel",
    },
  );
  if (!raw) return;
  if (!raw.includes("@") && raw.replace(/\D/g, "").startsWith("0")) {
    toast.error("Please use international format (e.g. 1...)");
    return;
  }
  const toNumber = normalizeWaId(raw);
  if (!toNumber) {
    toast.error("No phone number provided");
    return;
  }
  const token = await getToken(fromNumber);
  const promise = (async () => {
    // Send typing indication first
    const typingResult = await client({
      endpoint: "sendTypingIndication",
      payload: {
        fromNumber,
        chatId: toNumber,
        durationMs: 2000,
        token,
      },
    });
    if ("error" in typingResult) throw new Error(String(typingResult.error));

    // Wait a brief moment to simulate typing
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Send the actual message
    const res = await client({
      endpoint: "sendMessage",
      payload: {
        fromNumber,
        toNumber,
        message: "Hello World!",
        linkPreview: false,
        token,
      },
    });

    if ("error" in res) throw new Error(String(res.error));
    return res;
  })();

  await toast.promise(promise, {
    loading: "Typing and sending...",
    success: () => "Message sent",
    error: (e) => formatToastError(e, "Failed to send message"),
  });
};

const sendTelegramTest = async (phoneNumber: string) => {
  const raw = await promptDialog(
    "Enter a writable destination Telegram chat ID or @username:",
    { title: "Send Telegram test", placeholder: "@username or chat id" },
  );
  if (!raw) return;
  const chatId = raw.trim();
  const token = await getTelegramToken(phoneNumber);
  const promise = telegramClient({
    endpoint: "sendTelegramMessage",
    payload: { phoneNumber, chatId, text: "Hello World!", token },
  }).then((res) => {
    if ("error" in res) throw new Error(String(res.error));
    return res;
  });
  await toast.promise(promise, {
    loading: "Sending...",
    success: () => "Message sent",
    error: (e) =>
      e?.message ? `Failed: ${e.message}` : "Failed to send message",
  });
};

const listTelegramGroups = async (phoneNumber: string) => {
  const token = await getTelegramToken(phoneNumber);
  const promise = telegramClient({
    endpoint: "getTelegramGroups",
    payload: { phoneNumber, token },
  }).then((res) => {
    if ("error" in res) throw new Error(String(res.error));
    return res;
  });
  const result = await toast.promise(promise, {
    loading: "Loading groups...",
    success: "Groups loaded",
    error: (e) => e?.message || "Failed to list groups",
  });
  await alertDialog(
    result.groups.length === 0
      ? "No groups found"
      : result.groups.map((g) => `${g.name}: ${g.id}`).join("\n"),
    { title: "Telegram groups" },
  );
};

const reconnectingNumbers = new Set<string>();
const reconnectingListeners = new Set<() => void>();

const notifyReconnectingChanged = () =>
  reconnectingListeners.forEach((l) => l());

const useIsReconnecting = (number: string) => {
  const [isReconnecting, setIsReconnecting] = useState(
    reconnectingNumbers.has(number),
  );
  useEffect(() => {
    const listener = () => setIsReconnecting(reconnectingNumbers.has(number));
    reconnectingListeners.add(listener);
    return () => {
      reconnectingListeners.delete(listener);
    };
  }, [number]);
  return isReconnecting;
};

const isAssigningServerError = (message: string) =>
  message.startsWith("HTTP 202") && message.includes("assigning server");

const reconnectErrorMessage = (e: { message?: string }) => {
  const message = e?.message || "";
  if (isAssigningServerError(message)) {
    return "WhatsApp worker is still starting. Please try reconnect again in 2 minutes. If it still fails, contact support.";
  }
  return message ? `Failed: ${message}` : "Failed to trigger reconnect";
};

const reconnect = async (number: string) => {
  if (reconnectingNumbers.has(number)) return;
  reconnectingNumbers.add(number);
  notifyReconnectingChanged();
  try {
    const token = await getToken(number);
    const promise = client({
      endpoint: "reconnect",
      payload: { number, token },
    });
    await toast.promise(promise, {
      loading: "Reconnecting...",
      success: () => "Reconnect triggered",
      error: reconnectErrorMessage,
    });
  } finally {
    reconnectingNumbers.delete(number);
    notifyReconnectingChanged();
  }
};

const setLoginMethod = async (
  number: string,
  preferCodeLogin: boolean,
) => {
  const token = await getToken(number);
  await toast.promise(
    client({
      endpoint: "setLoginMethod",
      payload: { phoneNumber: number, preferCodeLogin, token },
    }),
    {
      loading: preferCodeLogin
        ? "Switching to code login..."
        : "Switching to QR login...",
      success: () =>
        preferCodeLogin ? "Switched to code login" : "Switched to QR login",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to switch login method",
    },
  );
};

const _setWarmupParticipation = async (
  refreshToken: string,
  phoneNumber: string,
  enabled: boolean,
) => {
  await toast.promise(
    userClient({
      endpoint: "setWarmupParticipation",
      payload: {
        refreshToken,
        phoneNumber,
        enabled,
      },
    }).then((result) => {
      if ("error" in result) throw new Error(result.error);
      return result;
    }),
    {
      loading: enabled
        ? "Joining warmup network..."
        : "Leaving warmup network...",
      success: () => enabled ? "Warmup enabled" : "Warmup disabled",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to update warmup",
    },
  );
};

const changeAccountName = async (phoneNumber: string) => {
  const name = await promptDialog("Enter new WhatsApp account name:", {
    title: "Change account name",
  });
  if (!name || !name.trim()) return;
  await toast.promise(
    client({
      endpoint: "changeWhatsappAccountName",
      payload: {
        phoneNumber,
        name: name.trim(),
        token: await getToken(phoneNumber),
      },
    }),
    {
      loading: "Changing account name...",
      success: () => "Account name changed",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to change account name",
    },
  );
};

const changeAccountBio = async (phoneNumber: string) => {
  const bio = await promptDialog("Enter new WhatsApp bio:", {
    title: "Change bio",
    helperText:
      "Note: If your bio isn't visible, make sure your 'About' privacy setting is set to 'Everyone' using the 'Privacy Settings' button below.",
  });
  if (bio === null) return;
  await toast.promise(
    client({
      endpoint: "changeWhatsappAccountBio",
      payload: {
        phoneNumber,
        bio: bio.trim(),
        token: await getToken(phoneNumber),
      },
    }),
    {
      loading: "Changing bio...",
      success: () => "Bio changed",
      error: (e) =>
        e?.message ? `Failed: ${e.message}` : "Failed to change bio",
    },
  );
};

const changeAccountPrivacy = async (phoneNumber: string) => {
  const category = await selectDialog(
    [
      {
        value: "about",
        label: "About / Bio Privacy",
        description: "Who can see your about text / bio",
      },
      {
        value: "profilePic",
        label: "Profile Photo Privacy",
        description: "Who can see your profile picture",
      },
    ],
    {
      title: "Choose Privacy Category",
      message: "Which privacy setting do you want to change?",
    },
  );
  if (!category) return;

  const value = await selectDialog(
    [
      {
        value: "all",
        label: "Everyone",
        description: "Visible to anyone who has your number",
      },
      {
        value: "contacts",
        label: "My Contacts",
        description: "Visible only to your WhatsApp contacts",
      },
      {
        value: "none",
        label: "Nobody",
        description: "Hidden from everyone",
      },
    ],
    {
      title: "Choose Privacy Level",
      message: `Who should be able to see your ${
        category === "about" ? "about / bio" : "profile photo"
      }?`,
    },
  );
  if (!value) return;

  await toast.promise(
    client({
      endpoint: "changeWhatsappPrivacySettings",
      payload: {
        phoneNumber,
        category: category as "about" | "profilePic",
        value: value as "all" | "contacts" | "none",
        token: await getToken(phoneNumber),
      },
    }),
    {
      loading: "Updating privacy setting...",
      success: () => "Privacy setting updated successfully",
      error: (e) =>
        e?.message
          ? `Failed: ${e.message}`
          : "Failed to update privacy setting",
    },
  );
};

const changeAccountAvatar = (phoneNumber: string) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result as string;
      if (!dataUrl) {
        toast.error("Failed to read image");
        return;
      }
      const token = await getToken(phoneNumber);
      const promise = client({
        endpoint: "changeWhatsappAccountAvatar",
        payload: { phoneNumber, imageBase64: dataUrl, token },
      });
      await toast.promise(promise, {
        loading: "Uploading avatar...",
        success: () =>
          "Avatar updated. If it doesn't show, make sure 'Profile Photo' privacy is set to 'Everyone' in 'Privacy Settings'.",
        error: (e) =>
          e?.message ? `Failed: ${e.message}` : "Failed to update avatar",
      });
    };
    reader.readAsDataURL(file);
  };
  input.click();
};

const editAccountName = async (
  accountId: string,
  currentName: string,
) => {
  const raw = await promptDialog("Edit account name:", {
    title: "Edit account name",
    defaultValue: currentName,
  });
  if (raw === null) return;
  const newName = raw.trim();
  if (newName === currentName) return;
  const promise = transact(
    tx.whatsappAccounts[accountId].update({ name: newName }),
  );
  await toast.promise(promise, {
    loading: "Saving name...",
    success: "Account name updated",
    error: (e) => e?.message || "Failed to update account name",
  });
};

const editAccountNotes = async (
  accountId: string,
  currentNotes?: string,
) => {
  const raw = await promptDialog("Edit notes:", {
    title: "Edit notes",
    defaultValue: currentNotes ?? "",
  });
  if (raw === null) return;
  const trimmed = raw.trim();
  const notes = trimmed === "" ? null : trimmed;
  const promise = transact(
    tx.whatsappAccounts[accountId].update({ notes }),
  );
  await toast.promise(promise, {
    loading: "Saving notes...",
    success: () => (notes ? "Notes updated" : "Notes removed"),
    error: (e) => e?.message || "Failed to update notes",
  });
};

const editDailyMessageCap = async (
  accountId: string,
  currentCap?: number | null,
) => {
  const current = currentCap ?? dailyMessageCap;
  const raw = await promptDialog(
    `Enter max daily message threshold (default: ${dailyMessageCap}):`,
    {
      title: "Edit max message threshold",
      defaultValue: String(current),
      inputMode: "numeric",
    },
  );
  if (raw === null) return;
  const trimmed = raw.trim();
  const parsed = trimmed === "" ? null : Number(trimmed);
  if (parsed !== null && (!Number.isInteger(parsed) || parsed <= 0)) {
    toast.error("Please enter a valid positive integer");
    return;
  }
  if (parsed === (currentCap ?? null)) return;
  const promise = transact(
    tx.whatsappAccounts[accountId].update({ dailyMessageCap: parsed }),
  );
  await toast.promise(promise, {
    loading: "Saving threshold...",
    success: () =>
      parsed !== null
        ? `Message threshold set to ${parsed}`
        : "Message threshold reset to default",
    error: (e) => e?.message || "Failed to update message threshold",
  });
};

const resetDailyMessageCap = async (accountId: string) => {
  const promise = transact(
    tx.whatsappAccounts[accountId].update({ dailyMessageCap: null }),
  );
  await toast.promise(promise, {
    loading: "Resetting threshold...",
    success: "Message threshold reset to default",
    error: (e) => e?.message || "Failed to reset message threshold",
  });
};

const mergeWebhooks = (
  webhook?: string,
  webhookUrls?: string[],
): string[] => [
  ...new Set([...(webhookUrls ?? []), ...(webhook ? [webhook] : [])]),
];

const handleAddWebhook =
  (phoneNumber: string, setUrls: (fn: (prev: string[]) => string[]) => void) =>
  async () => {
    const url = await promptDialog("Enter webhook URL:", {
      title: "Add webhook",
      placeholder: "https://example.com/webhook",
      inputMode: "url",
    });
    if (!url) return;
    const trimmed = url.trim();
    if (!trimmed) return;
    const token = await getToken(phoneNumber);
    const promise = client({
      endpoint: "addWebhook",
      payload: { phoneNumber, url: trimmed, token },
    });
    await toast.promise(promise, {
      loading: "Adding webhook...",
      success: () => "Webhook added",
      error: (e) => e?.message || "Failed to add webhook",
    });
    setUrls((prev) => [...new Set([...prev, trimmed])]);
  };

const handleRemoveWebhook = (
  phoneNumber: string,
  url: string,
  setUrls: (fn: (prev: string[]) => string[]) => void,
) =>
async () => {
  if (
    !await confirmDialog(`Remove webhook?\n${url}`, {
      title: "Remove webhook",
      okLabel: "Remove",
      danger: true,
    })
  ) return;
  const token = await getToken(phoneNumber);
  const promise = client({
    endpoint: "removeWebhook",
    payload: { phoneNumber, url, token },
  });
  await toast.promise(promise, {
    loading: "Removing webhook...",
    success: () => "Webhook removed",
    error: (e) => e?.message || "Failed to remove webhook",
  });
  setUrls((prev) => prev.filter((u) => u !== url));
};

const handleAddTelegramWebhook = (
  phoneNumber: string,
  setUrls: (fn: (prev: string[]) => string[]) => void,
) =>
async () => {
  const trimmed = (await promptDialog("Enter webhook URL:", {
    title: "Add webhook",
    placeholder: "https://example.com/webhook",
    inputMode: "url",
  }))?.trim();
  if (!trimmed) return;
  const token = await getTelegramToken(phoneNumber);
  const promise = telegramClient({
    endpoint: "addTelegramWebhook",
    payload: { phoneNumber, url: trimmed, token },
  });
  await toast.promise(promise, {
    loading: "Adding webhook...",
    success: () => "Webhook added",
    error: (e) => e?.message || "Failed to add webhook",
  });
  setUrls((prev) => [...new Set([...prev, trimmed])]);
};

const handleRemoveTelegramWebhook = (
  phoneNumber: string,
  url: string,
  setUrls: (fn: (prev: string[]) => string[]) => void,
) =>
async () => {
  if (
    !await confirmDialog(`Remove webhook?\n${url}`, {
      title: "Remove webhook",
      okLabel: "Remove",
      danger: true,
    })
  ) return;
  const token = await getTelegramToken(phoneNumber);
  const promise = telegramClient({
    endpoint: "removeTelegramWebhook",
    payload: { phoneNumber, url, token },
  });
  await toast.promise(promise, {
    loading: "Removing webhook...",
    success: () => "Webhook removed",
    error: (e) => e?.message || "Failed to remove webhook",
  });
  setUrls((prev) => prev.filter((u) => u !== url));
};

const webhookItemStyle = {
  fontSize: 12,
  opacity: 0.75,
  wordBreak: "break-all",
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const WebhookEditor = (
  { phoneNumber, webhook, webhookUrls }: {
    phoneNumber: string;
    webhook?: string;
    webhookUrls?: string[];
  },
) => {
  const [urls, setUrls] = useState(() => mergeWebhooks(webhook, webhookUrls));
  return (
    <div
      style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}
    >
      {urls.map((url) => (
        <div key={url} style={webhookItemStyle}>
          <span>{url}</span>
          <Button
            className="secondary"
            style={{ fontSize: 11, padding: "2px 6px" }}
            onClick={handleRemoveWebhook(phoneNumber, url, setUrls)}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        className="secondary"
        style={{ fontSize: 12, alignSelf: "flex-start" }}
        onClick={handleAddWebhook(phoneNumber, setUrls)}
      >
        + Add webhook
      </Button>
    </div>
  );
};

const TgWebhookEditor = (
  { phoneNumber, webhook, webhookUrls }: {
    phoneNumber: string;
    webhook?: string;
    webhookUrls?: string[];
  },
) => {
  const [urls, setUrls] = useState(() => mergeWebhooks(webhook, webhookUrls));
  return (
    <div
      style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}
    >
      {urls.map((url) => (
        <div key={url} style={webhookItemStyle}>
          <span>{url}</span>
          <Button
            className="secondary"
            style={{ fontSize: 11, padding: "2px 6px" }}
            onClick={handleRemoveTelegramWebhook(phoneNumber, url, setUrls)}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        className="secondary"
        style={{ fontSize: 12, alignSelf: "flex-start" }}
        onClick={handleAddTelegramWebhook(phoneNumber, setUrls)}
      >
        + Add webhook
      </Button>
    </div>
  );
};

const useCheckoutSuccess = (email?: string | null) => {
  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get("checkout") === "success") {
      const sessionId = params.get("session_id") || undefined;
      trackPurchaseConversion({ transactionId: sessionId, email });
      toast.success("Payment received! Your capacity will update shortly.");
      const url = new URL(globalThis.location.href);
      url.searchParams.delete("checkout");
      if (sessionId) url.searchParams.delete("session_id");
      globalThis.history.replaceState({}, "", url.toString());
    }
    if (params.get("billing") === "updated") {
      toast.success("Subscription updated. Your services will adjust shortly.");
      const url = new URL(globalThis.location.href);
      url.searchParams.delete("billing");
      globalThis.history.replaceState({}, "", url.toString());
    }
  }, [email]);
};

const startLineCheckout = (quantity = 1) => async (refreshToken: string) => {
  const checkoutClient = apiClient(
    httpCommunication(apiBaseUrl),
    { createCheckoutUrl: userApiDefinition.createCheckoutUrl },
  );
  const promise = checkoutClient({
    endpoint: "createCheckoutUrl",
    payload: { refreshToken, quantity },
  }).then((res) => {
    if ("error" in res) throw new Error(String(res.error));
    if ("url" in res) {
      globalThis.location.href = res.url;
      return;
    }
    throw new Error("No URL returned");
  });
  await toast.promise(promise, {
    loading: "Redirecting to checkout...",
    success: "Redirecting...",
    error: (e) => e?.message || "Failed to create checkout session",
  });
};

const openBillingPortal = async (refreshToken: string) => {
  const portalClient = apiClient(
    httpCommunication(apiBaseUrl),
    { createBillingPortalUrl: userApiDefinition.createBillingPortalUrl },
  );
  const promise = portalClient({
    endpoint: "createBillingPortalUrl",
    payload: { refreshToken },
  }).then((res) => {
    if ("error" in res) throw new Error(String(res.error));
    if ("url" in res) {
      globalThis.location.href = res.url;
      return;
    }
    throw new Error("No URL returned");
  });
  await toast.promise(promise, {
    loading: "Opening billing portal...",
    success: "Redirecting...",
    error: (e) => e?.message || "Failed to open billing portal",
  });
};

const startAuthenticatedPayment = (
  refreshToken: string | undefined,
  action: (refreshToken: string) => Promise<void>,
) =>
async () => {
  if (!refreshToken) {
    toast.error("Not authenticated");
    return;
  }
  await action(refreshToken);
};

const useBillingData = () => {
  const { user: authUser } = useAuth();
  const { data: servicesData } = useQuery(
    authUser?.email
      ? {
        whatsappAccounts: {
          $: { where: { isEnabled: true } },
        },
        telegramAccounts: {
          $: { where: { isEnabled: true } },
        },
        phoneNumbers: {
          $: { where: { status: "active" } },
        },
        fbAccounts: {
          $: { where: { status: { $in: ["active", "loginRequired"] } } },
        },
        linkedInAccounts: {
          $: { where: { status: { $in: ["active", "loginRequired"] } } },
        },
      }
      : null,
  );
  const { dbUser } = useUser();
  useCheckoutSuccess(authUser?.email);
  useEffect(() => {
    servicesData?.whatsappAccounts?.forEach((acc) => {
      if (acc.isConnected && acc.phoneNumber) {
        trackConnectedNumberConversion(acc.phoneNumber, authUser?.email);
      }
    });
  }, [servicesData?.whatsappAccounts, authUser?.email]);
  if (!dbUser) return null;
  const user = dbUser;

  const monthlyPayment = user.monthlyPayment || 0;
  const paidCapacityLegacy = Math.floor(monthlyPayment / monthlyPaymentPerLine);
  const paidCapacity = Math.max(paidCapacityLegacy, user.paidCapacity || 0);
  const phoneVerifiedAt = user.phoneVerifiedAt || 0;
  const trialEnd = new Date(phoneVerifiedAt + trialDurationMs);
  const isTrial = !!user.phoneVerifiedAt && Date.now() < trialEnd.getTime();
  const trialCapacity = isTrial ? 1 : 0;
  const capacity = Math.max(paidCapacity, trialCapacity);
  const budget = capacity * monthlyPaymentPerLine;
  const paidBudget = paidCapacity * monthlyPaymentPerLine;

  const waCount = servicesData?.whatsappAccounts?.length || 0;
  const tgCount = servicesData?.telegramAccounts?.length || 0;
  const numbers = servicesData?.phoneNumbers || [];
  const fbCount = servicesData?.fbAccounts?.length || 0;
  const linkedInCount = servicesData?.linkedInAccounts?.length || 0;
  const numberTotalPrice = numbers.reduce(
    (sum, n) => sum + (n.price || pbxmeNumberPriceUsd),
    0,
  );
  const spent = waCount * monthlyPaymentPerLine +
    tgCount * monthlyPaymentPerLine +
    numberTotalPrice + fbCount * monthlyPaymentPerLine +
    linkedInCount * monthlyPaymentPerLine;
  const remaining = budget - spent;
  const paidServiceSpend = numberTotalPrice +
    Math.max(0, waCount + tgCount + fbCount + linkedInCount - trialCapacity) *
      monthlyPaymentPerLine;
  const paidRemaining = Math.min(remaining, paidBudget - paidServiceSpend);

  return {
    user,
    authUser,
    waCount,
    tgCount,
    numbers,
    fbCount,
    linkedInCount,
    numberTotalPrice,
    spent,
    remaining,
    budget,
    capacity,
    trialCapacity,
    isTrial,
    trialEnd,
    paidRemaining,
  };
};

const BillingTab = () => {
  const data = useBillingData();
  const [manageOpen, setManageOpen] = useState(false);
  if (!data) return null;
  const {
    user: _user,
    authUser,
    waCount,
    tgCount,
    numbers,
    fbCount,
    linkedInCount,
    numberTotalPrice,
    spent,
    remaining,
    budget,
    capacity,
    trialCapacity,
    isTrial,
    trialEnd,
    paidRemaining,
  } = data;

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <h2 style={{ margin: 0 }}>Billing</h2>
      <div
        style={{
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          padding: 16,
          background: "var(--surface-alt)",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600 }}>
          Plan Summary
        </div>
        <div style={{ fontSize: 14, opacity: 0.9 }}>
          <div>
            Available budget: <strong>${budget}/mo</strong> ({capacity}{" "}
            slot{capacity === 1 ? "" : "s"})
          </div>
          {trialCapacity > 0 && (
            <div style={{ marginTop: 8, fontSize: 13, opacity: 0.8 }}>
              Free trial includes {trialCapacity}{" "}
              service slot for WhatsApp, Telegram, Facebook, or LinkedIn during
              your first month. Phone numbers require paid budget.
            </div>
          )}
          {spent > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ opacity: 0.85, fontWeight: 500 }}>Spent on:</div>
              {waCount > 0 && (
                <div style={{ paddingLeft: 12, marginTop: 2 }}>
                  - WhatsApp lines: {waCount} x ${monthlyPaymentPerLine}
                  = ${waCount * monthlyPaymentPerLine}
                </div>
              )}
              {tgCount > 0 && (
                <div style={{ paddingLeft: 12, marginTop: 2 }}>
                  - Telegram accounts: {tgCount} x ${monthlyPaymentPerLine}
                  = ${tgCount * monthlyPaymentPerLine}
                </div>
              )}
              {numbers.length > 0 && (
                <div style={{ paddingLeft: 12, marginTop: 2 }}>
                  - Phone numbers: {numbers.length} x ${pbxmeNumberPriceUsd}
                  = ${numberTotalPrice}
                </div>
              )}
              {fbCount > 0 && (
                <div style={{ paddingLeft: 12, marginTop: 2 }}>
                  - Facebook accounts: {fbCount} x ${monthlyPaymentPerLine}
                  = ${fbCount * monthlyPaymentPerLine}
                </div>
              )}
              {linkedInCount > 0 && (
                <div style={{ paddingLeft: 12, marginTop: 2 }}>
                  - LinkedIn accounts: {linkedInCount}{" "}
                  x ${monthlyPaymentPerLine}
                  = ${linkedInCount * monthlyPaymentPerLine}
                </div>
              )}
              <div style={{ marginTop: 4 }}>
                Total spent: <strong>${spent}</strong>
              </div>
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            Remaining: <strong>${remaining}</strong>
          </div>
          <div style={{ marginTop: 4, fontSize: 13, opacity: 0.8 }}>
            Paid number budget remaining: <strong>${paidRemaining}</strong>
          </div>
          {capacity === 0 && (
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>
              {isTrial
                ? (
                  <span>
                    Trial expires on {formatDateOnly(trialEnd)}
                  </span>
                )
                : (
                  <span>
                    You currently have 0 slots. Authenticate your personal phone
                    number via SMS to unlock 1 free slot for 1 month, or add
                    budget below.
                  </span>
                )}
            </div>
          )}
        </div>
        <div style={paymentOptionsStyle}>
          <div style={paymentOptionStyle}>
            <div style={paymentOptionTitleStyle}>
              Add budget
            </div>
            <div style={paymentOptionBodyStyle}>
              Purchase additional slots to add WhatsApp, Telegram, Facebook,
              LinkedIn, or SMS number capacity. Phone numbers always require
              paid budget.
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Button
                className="primary"
                style={{ fontSize: 14, padding: "8px 14px", width: "auto" }}
                onClick={startAuthenticatedPayment(
                  authUser?.refresh_token,
                  startLineCheckout(1),
                )}
              >
                + 1 Slot ($10/mo)
              </Button>
              <Button
                className="primary"
                style={{ fontSize: 14, padding: "8px 14px", width: "auto" }}
                onClick={startAuthenticatedPayment(
                  authUser?.refresh_token,
                  startLineCheckout(5),
                )}
              >
                + 5 Slots ($50/mo)
              </Button>
              <Button
                className="primary"
                style={{ fontSize: 14, padding: "8px 14px", width: "auto" }}
                onClick={startAuthenticatedPayment(
                  authUser?.refresh_token,
                  startLineCheckout(20),
                )}
              >
                + 20 Slots ($200/mo)
              </Button>
            </div>
            <div style={{ fontSize: 12, opacity: 0.65 }}>
              ${monthlyPaymentPerLine}/month per slot — adjust exact quantity in
              Stripe checkout
            </div>
            <Button
              className="secondary"
              style={{
                fontSize: 14,
                padding: "8px 16px",
                width: "auto",
                marginTop: 8,
              }}
              onClick={() => setManageOpen(true)}
            >
              Manage subscription
            </Button>
          </div>
        </div>
        <div
          style={{
            marginTop: 8,
            paddingTop: 12,
            borderTop: "1px solid var(--border)",
            fontSize: 13,
            opacity: 0.8,
          }}
        >
          One paid capacity slot covers any service type. The free trial covers
          one WhatsApp, Telegram, Facebook, or LinkedIn account. For billing
          questions, email{" "}
          <a
            href={`mailto:${supportEmail}`}
            style={{ color: "var(--primary)" }}
          >
            {supportEmail}
          </a>
        </div>
      </div>
      <ManageBillingModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        refreshToken={authUser?.refresh_token}
        waCount={waCount}
        tgCount={tgCount}
        fbCount={fbCount}
        linkedInCount={linkedInCount}
        numberCount={numbers.length}
      />
    </div>
  );
};

const dangerZoneCardStyle = {
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: 12,
  background: "var(--surface-alt)",
  display: "flex",
  flexDirection: "column" as const,
  gap: 10,
};

const dangerZoneTitleStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--text-muted)",
} as const;

const dangerZoneButtonStyle = {
  fontSize: 12,
  padding: "5px 12px",
  borderColor: "rgba(239, 68, 68, 0.4)",
  color: "rgba(239, 68, 68, 0.8)",
};

const handleDeleteUserAccount = async (refreshToken: string) => {
  const confirmed = await confirmDialog(
    "Are you sure you want to permanently delete your account and all data? This will immediately cancel all subscriptions, delete all connected accounts, lines, phone numbers, and wipe all your data permanently. This action cannot be undone.",
    {
      title: "Delete Account & Data Completely",
      okLabel: "Continue",
      danger: true,
    },
  );
  if (!confirmed) return;

  const phrase = await promptDialog(
    'Please type "DELETE" to confirm permanent deletion of your account and all data:',
    {
      title: "Confirm Account Deletion",
      placeholder: "DELETE",
      okLabel: "Delete Account & Data",
      cancelLabel: "Cancel",
    },
  );
  if (phrase !== "DELETE") {
    if (phrase !== null) {
      toast.error('You must type "DELETE" to confirm account deletion.');
    }
    return;
  }

  await toast.promise(
    userClient({
      endpoint: "deleteUserAccount",
      payload: { refreshToken },
    }).then(async (res) => {
      if ("error" in res) throw new Error(String(res.error));
      await auth.signOut();
      globalThis.location.href = "/";
      return res;
    }),
    {
      loading: "Deleting account and all data...",
      success: "Your account and data have been completely deleted",
      error: (e) => e?.message || "Failed to delete account",
    },
  );
};

const SettingsTab = () => {
  const { dbUser } = useUser();
  const { user: authUser } = useAuth();
  if (!dbUser) return null;
  const user = dbUser;

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <h2 style={{ margin: 0 }}>Settings</h2>
      <div
        style={{
          ...accountCardStyle,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, opacity: 0.85 }}>
              Your Personal WhatsApp (alerts & test recipient):
            </span>
            <span style={{ fontWeight: 600 }}>
              {user.whatsapp || "Not set"}
            </span>
            <Button
              className="secondary"
              style={{ fontSize: 12, padding: "4px 12px" }}
              onClick={async () => {
                const raw = await promptDialog(
                  "Enter your WhatsApp number (e.g., 15551234567):",
                  {
                    title: user.whatsapp
                      ? "Edit your WhatsApp number"
                      : "Add your WhatsApp number",
                    defaultValue: user.whatsapp || "",
                    placeholder: "15551234567",
                    inputMode: "tel",
                  },
                );
                if (raw === null) return;
                const trimmed = raw.replace(/\D/g, "");
                if (trimmed.startsWith("0")) {
                  toast.error("Please use international format (e.g. 1...)");
                  return;
                }
                const whatsapp = trimmed === "" ? null : trimmed;
                const promise = transact(
                  tx.users[user.id].update({ whatsapp }),
                );
                await toast.promise(promise, {
                  loading: "Saving...",
                  success: () =>
                    whatsapp
                      ? "WhatsApp number updated"
                      : "WhatsApp number removed",
                  error: (e) => e?.message || "Failed to save",
                });
              }}
            >
              {user.whatsapp
                ? <PencilIcon size={11} />
                : <PlusIcon size={11} />}
              {user.whatsapp ? "Edit" : "Add"}
            </Button>
          </div>
          <div
            style={{
              fontSize: 12,
              opacity: 0.7,
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <AlertTriangleIcon size={13} style={{ color: "#f59e0b" }} />
            <span>
              Use this for receiving test messages and communicating with
              support. This is NOT your bot line — to connect a bot account, go
              to the <strong>WhatsApp</strong> tab above.
            </span>
          </div>
        </div>
      </div>
      <div style={dangerZoneCardStyle}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <AlertTriangleIcon
              size={13}
              style={{ color: "var(--text-muted)" }}
            />
            <span style={dangerZoneTitleStyle}>Danger Zone</span>
          </div>
          <span style={{ fontSize: 12, opacity: 0.7 }}>
            Permanently delete your account and all associated data, including
            all connected lines, phone numbers, and active subscriptions.
          </span>
        </div>
        <div>
          <Button
            className="secondary"
            style={dangerZoneButtonStyle}
            onClick={() => authUser?.refresh_token &&
              handleDeleteUserAccount(authUser.refresh_token)}
          >
            <TrashIcon size={13} /> Delete Account & Data
          </Button>
        </div>
      </div>
    </div>
  );
};

const yellowStatuses = new Set<string>([
  whatsappAccountStatus.waitingForSsh,
  whatsappAccountStatus.startingDocker,
  whatsappAccountStatus.provisioningServer,
  whatsappAccountStatus.serverProvisioned,
  whatsappAccountStatus.dockerStarted,
  "clientCreated",
  "streamMode:QR",
  "awaitingCode",
  "streamMode:SYNCING",
  "streamMode:OFFLINE",
]);

const connectingStatuses = new Set<string>([
  whatsappAccountStatus.waitingForSsh,
  whatsappAccountStatus.startingDocker,
  whatsappAccountStatus.provisioningServer,
  whatsappAccountStatus.serverProvisioned,
  whatsappAccountStatus.dockerStarted,
  "clientCreated",
  "streamMode:SYNCING",
]);

const isConnectingStatus = (status: string) => connectingStatuses.has(status);

const isYellowStatus = (status: string) =>
  yellowStatuses.has(status) || status.startsWith("qrAttempt:");

const greenStatuses = new Set<string>([
  whatsappAccountStatus.connected,
  "connected",
  "isLogged",
  "qrReadSuccess",
  "streamMode:MAIN",
]);

const redStatuses = new Set<string>([
  whatsappAccountStatus.podIsDown,
  "streamMode:CONFLICT",
  "streamMode:PROXYBLOCK",
  "streamMode:TOS_BLOCK",
  "streamMode:SMB_TOS_BLOCK",
  "streamMode:DEPRECATED_VERSION",
  "pairingCodeRateLimited",
  "loginFailed",
  "dailyMessageLimitReached",
]);

const browserLaunchFailedReason = (status: string | undefined) =>
  status?.startsWith("browserLaunchFailed:")
    ? status.slice("browserLaunchFailed:".length)
    : null;

const statusEntryColor = (status: string) =>
  greenStatuses.has(status)
    ? "#2e7d32"
    : isYellowStatus(status)
    ? "#f57c00"
    : redStatuses.has(status)
    ? "#ff5555"
    : "#c62828";

const connectingWindowMs = 60_000;

const accountFromHash = () => {
  if (typeof globalThis.location === "undefined") return undefined;
  const [, account] = globalThis.location.hash.replace(/^#/, "").split("/");
  return account ? decodeURIComponent(account) : undefined;
};

const useIsDeepLinkTarget = (identifier: string) => {
  const [isTarget, setIsTarget] = useState(
    () => accountFromHash() === identifier,
  );
  useEffect(() => {
    const onHashChange = () => setIsTarget(accountFromHash() === identifier);
    globalThis.addEventListener("hashchange", onHashChange);
    return () => globalThis.removeEventListener("hashchange", onHashChange);
  }, [identifier]);
  return isTarget;
};

// Expands and scrolls an account card into view when it is the hash target.
const useDeepLinkExpand = (
  identifier: string,
  setExpanded: (expanded: boolean) => void,
) => {
  const ref = useRef<HTMLLIElement>(null);
  const isTarget = useIsDeepLinkTarget(identifier);
  useEffect(() => {
    if (!isTarget) return;
    setExpanded(true);
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [isTarget]);
  return ref;
};

const useNow = (intervalMs: number) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
};

const hasReachedDailyLimit = (
  statusHistory: Array<{ status: string; timestamp: number }> | undefined,
  now: number,
) => {
  if (!statusHistory) return false;
  const lastLimitEvent = [...statusHistory]
    .reverse()
    .find((e) => e.status === "dailyMessageLimitReached");
  if (!lastLimitEvent) return false;

  const d1 = new Date(lastLimitEvent.timestamp);
  const d2 = new Date(now);
  return (
    d1.getUTCDate() === d2.getUTCDate() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCFullYear() === d2.getUTCFullYear()
  );
};

const isConnectionResetEvent = (status: string) =>
  status === "manualReconnectRequested" ||
  status.startsWith("qrAttempt:") ||
  status === "streamMode:MAIN" ||
  status === "isLogged" ||
  status === "inChat";

const getMismatchInfo = (
  statusHistory: Array<{ status: string; timestamp: number }> | undefined,
) => {
  const reversed = [...(statusHistory ?? [])].reverse();
  const resetIdx = reversed.findIndex((e) => isConnectionResetEvent(e.status));
  const relevantEvents = resetIdx === -1
    ? reversed
    : reversed.slice(0, resetIdx);
  const mismatchEvent = relevantEvents.find((e) =>
    e.status.startsWith("phoneNumberMismatch")
  );
  if (!mismatchEvent) return null;
  const parts = mismatchEvent.status.split(":");
  return {
    timestamp: mismatchEvent.timestamp,
    scannedNumber: parts.length > 1 && parts[1] ? parts[1] : undefined,
  };
};

const getConnectionDisplay = (
  isConnected: boolean,
  statusHistory: Array<{ status: string; timestamp: number }> | undefined,
  now: number,
) => {
  if (hasReachedDailyLimit(statusHistory, now)) {
    return { label: "Throttled (Limit Reached)", color: "#ff5555" };
  }
  if (isConnected) return { label: "Connected", color: "#2e7d32" };
  const last = statusHistory?.[statusHistory.length - 1];
  if (
    last &&
    (last.status === "streamMode:TOS_BLOCK" ||
      last.status === "streamMode:SMB_TOS_BLOCK")
  ) {
    return { label: "Banned", color: "#ff5555" };
  }
  if (getMismatchInfo(statusHistory)) {
    return { label: "Phone Mismatch", color: "#ff5555" };
  }
  if (
    last &&
    isConnectingStatus(last.status) &&
    now - last.timestamp < connectingWindowMs
  ) {
    return { label: "Connecting", color: "#f57c00" };
  }
  return { label: "Disconnected", color: "#c62828" };
};

const DowntimeDetailsModal = ({
  open,
  onClose,
  phoneNumber,
  name,
  statusHistory,
  lastEventTime,
  now,
}: {
  open: boolean;
  onClose: () => void;
  phoneNumber?: string;
  name?: string;
  statusHistory: Array<{ status: string; timestamp: number }> | undefined;
  lastEventTime: number | null | undefined;
  now: number;
}) => {
  if (!open) return null;

  const downtimeMs = downtimeMsLast24h(statusHistory, now, lastEventTime);
  const uptimePct = uptimePercentageLast24h(statusHistory, now, lastEventTime);
  const bins = downtimeBinsLast24h(statusHistory, now);
  const accountName = name || phoneNumber || "Account";

  const getCategoryColor = (category: "green" | "yellow" | "red" | "gray") => {
    switch (category) {
      case "green":
        return "#2e7d32";
      case "yellow":
        return "#ff9800";
      case "red":
        return "#f44336";
      case "gray":
        return "rgba(255, 255, 255, 0.15)";
    }
  };

  const getCategoryLabel = (category: "green" | "yellow" | "red" | "gray") => {
    switch (category) {
      case "green":
        return "Connected / Operational";
      case "yellow":
        return "Connecting / Transisting";
      case "red":
        return "Disconnected / Down";
      case "gray":
        return "No history";
    }
  };

  const [selectedBinIndex, setSelectedBinIndex] = useState<number | null>(null);

  const windowStart = now - 24 * 60 * 60 * 1000;
  const recentEvents = [...(statusHistory || [])]
    .filter((e) => e.timestamp >= windowStart)
    .reverse();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Connection History (${accountName})`}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Summary metrics */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            background: "rgba(255, 255, 255, 0.03)",
            padding: 12,
            borderRadius: "var(--radius)",
            border: "1px solid var(--border)",
          }}
        >
          <div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              24h Estimated Uptime
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: uptimePct > 99
                  ? "#81c784"
                  : uptimePct > 95
                  ? "#ffb74d"
                  : "#e57373",
              }}
            >
              {uptimePct.toFixed(1)}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>24h Total Downtime</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: downtimeMs > 0 ? "#ffb74d" : "#81c784",
              }}
            >
              {formatDowntime(downtimeMs)}
            </div>
          </div>
        </div>

        {/* 24-hour visual bar */}
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 8,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>24-Hour Timeline</span>
            <span style={{ fontSize: 11, opacity: 0.6 }}>
              Tap an hour to inspect
            </span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(24, 1fr)",
              gap: 3,
              height: 28,
              background: "rgba(0, 0, 0, 0.2)",
              padding: 4,
              borderRadius: 6,
            }}
          >
            {bins.map((bin, idx) => {
              const isSelected = selectedBinIndex === idx;
              return (
                <button
                  type="button"
                  key={idx}
                  onClick={() => setSelectedBinIndex(isSelected ? null : idx)}
                  style={{
                    background: getCategoryColor(bin.category),
                    border: isSelected
                      ? "2px solid #fff"
                      : "1px solid transparent",
                    borderRadius: 2,
                    cursor: "pointer",
                    padding: 0,
                    height: "100%",
                    outline: "none",
                  }}
                  title={`Hour ${idx + 1}/24: ${
                    getCategoryLabel(bin.category)
                  }`}
                />
              );
            })}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 10,
              opacity: 0.5,
              marginTop: 4,
            }}
          >
            <span>24h ago</span>
            <span>12h ago</span>
            <span>Now</span>
          </div>
        </div>

        {/* Selected Hour Details if tapped */}
        {selectedBinIndex !== null && (
          <div
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              padding: 10,
              borderRadius: 6,
              border: "1px solid var(--border)",
              fontSize: 12,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Hour Range:{" "}
              {new Date(bins[selectedBinIndex].start).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })} -{" "}
              {new Date(bins[selectedBinIndex].end).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </div>
            <div style={{ opacity: 0.8 }}>
              Status: {getCategoryLabel(bins[selectedBinIndex].category)}
            </div>
            {bins[selectedBinIndex].events.length > 0
              ? (
                <div style={{ marginTop: 6 }}>
                  <div
                    style={{ fontWeight: 600, fontSize: 11, marginBottom: 4 }}
                  >
                    Events in this hour:
                  </div>
                  {bins[selectedBinIndex].events.map((e, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "2px 0",
                        borderBottom: "1px dotted rgba(255, 255, 255, 0.1)",
                      }}
                    >
                      <span
                        style={{
                          color: statusEntryColor(e.status),
                          fontWeight: 500,
                        }}
                      >
                        {e.status}
                      </span>
                      <span style={{ opacity: 0.7 }}>
                        {new Date(e.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              )
              : (
                <div style={{ opacity: 0.6, fontSize: 11, marginTop: 4 }}>
                  No status transitions in this hour interval.
                </div>
              )}
          </div>
        )}

        {/* Full 24h Event History Log */}
        <div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              marginBottom: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>24h Event Log ({recentEvents.length})</span>
            {lastEventTime && (
              <span style={{ fontSize: 11, opacity: 0.6, fontWeight: 400 }}>
                Last event: {new Date(lastEventTime).toLocaleTimeString()}
              </span>
            )}
          </div>

          {recentEvents.length > 0
            ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  maxHeight: 220,
                  overflowY: "auto",
                  padding: 8,
                  background: "rgba(0, 0, 0, 0.2)",
                  borderRadius: "var(--radius)",
                }}
              >
                {recentEvents.map((entry, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: 12,
                      padding: "4px 6px",
                      borderRadius: 4,
                      background: "rgba(255, 255, 255, 0.02)",
                    }}
                  >
                    <span
                      style={{
                        color: statusEntryColor(entry.status),
                        fontWeight: 600,
                      }}
                    >
                      {entry.status}
                    </span>
                    <span style={{ opacity: 0.7, fontSize: 11 }}>
                      {formatDate(entry.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            )
            : (
              <div
                style={{
                  padding: 12,
                  textAlign: "center",
                  fontSize: 12,
                  opacity: 0.6,
                  background: "rgba(0, 0, 0, 0.15)",
                  borderRadius: "var(--radius)",
                }}
              >
                No status changes in the last 24 hours.
              </div>
            )}
        </div>
      </div>
    </Modal>
  );
};

const DowntimeBadge = (
  {
    phoneNumber,
    name,
    statusHistory,
    lastEventTime,
    now,
    className,
    style,
  }: {
    phoneNumber?: string;
    name?: string;
    statusHistory: Array<{ status: string; timestamp: number }> | undefined;
    lastEventTime: number | null | undefined;
    now: number;
    className?: string;
    style?: React.JSX.CSSProperties;
  },
) => {
  const [modalOpen, setModalOpen] = useState(false);
  const _downtimeMs = downtimeMsLast24h(statusHistory, now, lastEventTime);
  const uptimePct = uptimePercentageLast24h(statusHistory, now, lastEventTime);
  const bins = downtimeBinsLast24h(statusHistory, now);

  const getCategoryColor = (category: "green" | "yellow" | "red" | "gray") => {
    switch (category) {
      case "green":
        return "#2e7d32";
      case "yellow":
        return "#ff9800";
      case "red":
        return "#f44336";
      case "gray":
        return "rgba(255, 255, 255, 0.15)";
    }
  };

  return (
    <>
      <div
        onClick={(e) => {
          e.stopPropagation();
          setModalOpen(true);
        }}
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          marginLeft: "auto",
          padding: "3px 8px",
          borderRadius: "4px",
          background: "rgba(255, 255, 255, 0.04)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          cursor: "pointer",
          userSelect: "none",
          ...style,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: uptimePct > 99
              ? "#81c784"
              : uptimePct > 95
              ? "#ffb74d"
              : "#e57373",
          }}
        >
          {uptimePct.toFixed(1)}% (last 24h)
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {bins.map((bin, idx) => (
            <div
              key={idx}
              style={{
                width: 4,
                height: 14,
                borderRadius: "1px",
                backgroundColor: getCategoryColor(bin.category),
              }}
            />
          ))}
        </div>
      </div>

      <DowntimeDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        phoneNumber={phoneNumber}
        name={name}
        statusHistory={statusHistory}
        lastEventTime={lastEventTime}
        now={now}
      />
    </>
  );
};

const statusHistoryListStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: 4,
  maxHeight: "200px",
  overflowY: "auto" as const,
  padding: "8px",
  background: "rgba(0,0,0,0.2)",
  borderRadius: "var(--radius)",
  fontSize: 12,
} as const;

const StatusHistorySection = (
  { history }: {
    history: Array<{ status: string; timestamp: number }> | undefined;
  },
) => {
  if (!history || history.length === 0) return null;
  return (
    <CollapsibleSection title="Status History">
      <div style={statusHistoryListStyle}>
        {[...history].reverse().map((entry, idx) => (
          <div key={idx} style={{ opacity: 0.85, fontSize: 11 }}>
            <span
              style={{ color: statusEntryColor(entry.status), fontWeight: 500 }}
            >
              {entry.status}
            </span>{" "}
            <span style={{ opacity: 0.7 }}>{formatDate(entry.timestamp)}</span>
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );
};

const isoFlagEmoji = (iso: string) => {
  if (!iso || iso.length !== 2) return "";
  const base = 0x1f1e6;
  const A = "A".charCodeAt(0);
  return String.fromCodePoint(
    base + iso.toUpperCase().charCodeAt(0) - A,
    base + iso.toUpperCase().charCodeAt(1) - A,
  );
};

const mismatchBannerStyle = {
  padding: "10px 14px",
  background: "rgba(239, 68, 68, 0.08)",
  border: "1px solid rgba(239, 68, 68, 0.25)",
  borderRadius: "var(--radius)",
  marginTop: 6,
  display: "flex",
  flexDirection: "column" as const,
  gap: 8,
};

const mismatchBannerHeaderStyle = {
  color: "#ef4444",
  fontWeight: 600,
  fontSize: 13,
};

const mismatchBannerBodyStyle = {
  lineHeight: 1.4,
  opacity: 0.9,
  fontSize: 12,
};

const mismatchBannerActionStyle = {
  display: "flex",
  gap: 8,
  marginTop: 2,
};

const mismatchSmallButtonStyle = {
  fontSize: 12,
  padding: "6px 12px",
};

const mismatchBoxStyle = {
  padding: "14px 16px",
  background: "rgba(239, 68, 68, 0.08)",
  border: "1px solid rgba(239, 68, 68, 0.25)",
  borderRadius: "var(--radius)",
  display: "flex",
  flexDirection: "column" as const,
  gap: 8,
};

const mismatchNumberRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 12px",
  background: "rgba(0, 0, 0, 0.2)",
  borderRadius: "var(--radius)",
  fontSize: 13,
};

const PhoneNumberMismatchModal = ({
  open,
  onClose,
  registeredPhone,
  scannedPhone,
  name,
  notes,
  refreshToken,
}: {
  open: boolean;
  onClose: () => void;
  registeredPhone: string;
  scannedPhone?: string;
  name: string;
  notes?: string;
  refreshToken?: string;
}) => {
  const [isSwitching, setIsSwitching] = useState(false);

  const handleSwitchLine = async () => {
    if (!refreshToken || !scannedPhone) return;
    setIsSwitching(true);
    try {
      await toast.promise(
        (async () => {
          const delRes = await userClient({
            endpoint: "deleteAccount",
            payload: { refreshToken, phoneNumber: registeredPhone },
          });
          if ("error" in delRes) throw new Error(String(delRes.error));

          const createRes = await userClient({
            endpoint: "createAccount",
            payload: {
              refreshToken,
              phoneNumber: scannedPhone,
              name: name === registeredPhone ? scannedPhone : name,
              notes,
            },
          });
          if ("error" in createRes) throw new Error(String(createRes.error));

          await reconnect(scannedPhone);
        })(),
        {
          loading: `Switching line to +${scannedPhone}...`,
          success: () => `Line switched to +${scannedPhone}`,
          error: (e) =>
            e instanceof Error ? e.message : "Failed to switch line",
        },
      );
      onClose();
    } finally {
      setIsSwitching(false);
    }
  };

  const handleRetryRegistered = async () => {
    await reconnect(registeredPhone);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Wrong Phone Number Scanned">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={mismatchBoxStyle}>
          <div style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.9 }}>
            {scannedPhone
              ? `You scanned the QR code with WhatsApp on a phone registered as +${scannedPhone}, but this line was created for +${registeredPhone}.`
              : `You scanned the QR code with a phone registered to a different number than +${registeredPhone}.`}
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, lineHeight: 1.4 }}>
            WhatsApp lines in Supergreen must match the phone number of the
            scanned device. The session was disconnected automatically.
          </div>
        </div>

        {scannedPhone && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={mismatchNumberRowStyle}>
              <span style={{ opacity: 0.7 }}>Registered line:</span>
              <span style={{ fontWeight: 600, fontFamily: "monospace" }}>
                +{registeredPhone}
              </span>
            </div>
            <div style={mismatchNumberRowStyle}>
              <span style={{ opacity: 0.7 }}>Scanned device:</span>
              <span
                style={{
                  fontWeight: 600,
                  color: "#10b981",
                  fontFamily: "monospace",
                }}
              >
                +{scannedPhone}
              </span>
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginTop: 4,
          }}
        >
          {scannedPhone && (
            <Button
              className="primary"
              disabled={isSwitching}
              onClick={handleSwitchLine}
            >
              {isSwitching ? "Switching..." : `Switch line to +${scannedPhone}`}
            </Button>
          )}
          <Button
            className="secondary"
            disabled={isSwitching}
            onClick={handleRetryRegistered}
          >
            Scan again with +{registeredPhone}
          </Button>
          <Button
            className="secondary"
            style={{ opacity: 0.7 }}
            onClick={onClose}
          >
            Dismiss
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const WaAccount = (
  {
    account: {
      id,
      admins,
      secretToken,
      webhook,
      verificationCode,
      pairingCode,
      pairingCodeIssuedAt,
      qrIssuedAt,
      webhookUrls,
      name,
      notes,
      lastEventTime,
      preferCodeLogin,
      nextWarmupAt: _nextWarmupAt,
      isEnabled,
      statusHistory,
      phoneNumber,
      isConnected,
      avatarUrl,
      dailyMessageCap: accountDailyMessageCap,
    },
  }: {
    account: InstaQLEntity<
      typeof schema,
      "whatsappAccounts",
      // deno-lint-ignore ban-types
      { server: {}; admins: {} }
    >;
  },
) => {
  const { user: authUser } = useAuth();
  const { dbUser } = useUser();
  const now = useNow(5000);
  useEffect(() => {
    if (isConnected && phoneNumber) {
      trackConnectedNumberConversion(phoneNumber, authUser?.email);
    }
  }, [isConnected, phoneNumber, authUser?.email]);
  const lastStatusItem = statusHistory?.[statusHistory.length - 1];
  const lastStatus = lastStatusItem?.status;
  const isReconnecting = useIsReconnecting(phoneNumber);
  const isBusy = isReconnecting || !!(
    lastStatus &&
    lastStatusItem &&
    now - lastStatusItem.timestamp < 2 * 60 * 1000 &&
    [
      "provisioningServer",
      whatsappAccountStatus.waitingForSsh,
      "startingDocker",
    ].includes(lastStatus)
  );
  const mismatchInfo = !isConnected ? getMismatchInfo(statusHistory) : null;
  const launchFailureReason = isConnected
    ? null
    : browserLaunchFailedReason(lastStatus);
  const [dismissedMismatchTime, setDismissedMismatchTime] = useState<
    number | null
  >(null);
  const showMismatchModal = Boolean(
    mismatchInfo && mismatchInfo.timestamp !== dismissedMismatchTime,
  );
  const [expanded, setExpanded] = useState(false);
  const waCardRef = useDeepLinkExpand(phoneNumber, setExpanded);
  return (
    <li key={id} ref={waCardRef} style={accountCardStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          userSelect: "none",
          flexWrap: "wrap",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            opacity: 0.5,
          }}
        >
          {expanded
            ? <ChevronDownIcon size={13} />
            : <ChevronRightIcon size={13} />}
        </span>
        {avatarUrl
          ? (
            <img
              src={avatarUrl}
              alt=""
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          )
          : (
            <div
              title="If your avatar isn't showing, change your WhatsApp privacy settings for 'Profile Photo' to 'Everyone' so our workers can fetch it."
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "var(--surface-alt)",
                border: "1px dashed var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "var(--text-muted)",
                cursor: "help",
              }}
            >
              <UserIcon size={13} />
            </div>
          )}
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          {name || "No name"}
        </span>
        <span
          style={{
            color: getConnectionDisplay(!!isConnected, statusHistory, now)
              .color,
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {getConnectionDisplay(!!isConnected, statusHistory, now).label}
        </span>
        <DowntimeBadge
          phoneNumber={phoneNumber}
          name={name}
          statusHistory={statusHistory}
          lastEventTime={lastEventTime}
          now={now}
          className="downtime-badge-desktop"
        />
        {isEnabled === false && (
          <span
            style={{
              color: "#ff9800",
              fontWeight: 600,
              fontSize: 12,
              border: "1px solid #ff9800",
              padding: "2px 6px",
              borderRadius: "4px",
            }}
          >
            SUSPENDED
          </span>
        )}
      </div>
      {expanded && (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div className="downtime-badge-mobile">
            <DowntimeBadge
              phoneNumber={phoneNumber}
              name={name}
              statusHistory={statusHistory}
              lastEventTime={lastEventTime}
              now={now}
              style={{ marginLeft: 0 }}
            />
          </div>

          <div
            style={{
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontWeight: 500,
            }}
          >
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <PhoneIcon size={13} /> Phone:
            </span>
            <span style={{ fontWeight: 600 }}>
              {formatPhone(phoneNumber)}
            </span>
          </div>

          {lastEventTime && (
            <div style={{ fontSize: 13, opacity: 0.85 }}>
              Last event: {formatDate(lastEventTime)}
            </div>
          )}

          <div
            style={{
              fontSize: 13,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span>{notes ? `Notes: ${notes}` : "No notes"}</span>
              <Button
                className="secondary"
                style={{ fontSize: 10, padding: "2px 6px" }}
                onClick={() => editAccountNotes(id, notes)}
              >
                {notes ? <PencilIcon size={11} /> : <PlusIcon size={11} />}
                {notes ? "Edit Notes" : "Add Notes"}
              </Button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span>{name ? `Name: ${name}` : "No name set"}</span>
              <Button
                className="secondary"
                style={{ fontSize: 10, padding: "2px 6px" }}
                onClick={() => editAccountName(id, name ?? "")}
              >
                {name ? <PencilIcon size={11} /> : <PlusIcon size={11} />}
                {name ? "Edit Name" : "Set Name"}
              </Button>
            </div>
          </div>

          {secretToken && <SecretToken token={secretToken} />}
          <CollapsibleSection
            title={`Webhooks (${mergeWebhooks(webhook, webhookUrls).length})`}
          >
            <WebhookEditor
              phoneNumber={phoneNumber}
              webhook={webhook}
              webhookUrls={webhookUrls}
            />
          </CollapsibleSection>
          <CollapsibleSection title={`Admins (${(admins || []).length})`}>
            <div style={adminsRowStyle}>
              {(admins && admins.length > 0)
                ? admins.map((admin) => (
                  <span style={adminChipStyle} key={admin.id}>
                    {admin.email}
                    <Button
                      className="secondary"
                      style={{ fontSize: 10, padding: "2px 6px" }}
                      onClick={() =>
                        removeAdmin(id, admin.id, admin.email)}
                    >
                      <TrashIcon size={11} /> Remove
                    </Button>
                  </span>
                ))
                : <span>None</span>}
              <Button
                className="secondary"
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={() =>
                  addAdmin(
                    id,
                    (admins || []).map((a: { email: string }) => a.email),
                  )}
              >
                <PlusIcon size={11} /> Add Admin
              </Button>
            </div>
          </CollapsibleSection>
          <CollapsibleSection title="Advanced">
            <div style={advancedSectionStyle}>
              <div style={thresholdRowStyle}>
                <span>
                  Max message threshold:{" "}
                  <strong>{accountDailyMessageCap ?? dailyMessageCap}</strong>
                  {accountDailyMessageCap ? " (custom)" : " (default)"}
                </span>
                <Button
                  className="secondary"
                  style={{ fontSize: 10, padding: "2px 6px" }}
                  onClick={() =>
                    editDailyMessageCap(id, accountDailyMessageCap)}
                >
                  <PencilIcon size={11} /> Edit Threshold
                </Button>
                {accountDailyMessageCap && (
                  <Button
                    className="secondary"
                    style={{ fontSize: 10, padding: "2px 6px" }}
                    onClick={() => resetDailyMessageCap(id)}
                  >
                    Reset to default
                  </Button>
                )}
              </div>
              <span style={{ opacity: 0.7, fontSize: 11 }}>
                Limits outgoing messages per 24h (resets at midnight UTC).
              </span>
            </div>
          </CollapsibleSection>

          <StatusHistorySection history={statusHistory} />

          {!isConnected &&
            verificationCode && (
            <div style={{ marginTop: 4 }}>
              <QrWithTimer
                size={180}
                url={verificationCode}
                issuedAt={coerce(qrIssuedAt)}
              />
              <div
                style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}
              >
                Scan with WhatsApp on your phone
              </div>
            </div>
          )}
          {!isConnected &&
            pairingCode && (
            <div style={{ marginTop: 4 }}>
              <PairingCodeWithTimer
                code={pairingCode}
                issuedAt={coerce(pairingCodeIssuedAt)}
              />
              <div
                style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}
              >
                Enter this code in WhatsApp: Settings → Linked Devices → Link a
                Device → Link with phone number
              </div>
            </div>
          )}
          {!isConnected &&
            !verificationCode &&
            !pairingCode && (
            <div
              style={{ fontSize: 12, opacity: 0.75 }}
            >
              {mismatchInfo
                ? (
                  <div style={mismatchBannerStyle}>
                    <div
                      style={{
                        ...mismatchBannerHeaderStyle,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <AlertTriangleIcon
                        size={15}
                        style={{ color: "#ef4444" }}
                      />
                      Wrong phone number scanned
                    </div>
                    <div style={mismatchBannerBodyStyle}>
                      {mismatchInfo.scannedNumber
                        ? `WhatsApp on +${mismatchInfo.scannedNumber} was scanned, but this line is registered for +${phoneNumber}.`
                        : `A phone with a different number was scanned instead of +${phoneNumber}.`}
                    </div>
                    <div style={mismatchBannerActionStyle}>
                      <Button
                        className="primary"
                        style={mismatchSmallButtonStyle}
                        onClick={() => setDismissedMismatchTime(null)}
                      >
                        Resolve mismatch
                      </Button>
                      <Button
                        className="secondary"
                        style={mismatchSmallButtonStyle}
                        onClick={() => reconnect(phoneNumber)}
                      >
                        Generate new QR
                      </Button>
                    </div>
                  </div>
                )
                : lastStatus === "pairingCodeRateLimited"
                ? (
                  <span style={{ color: "#ff5555", fontWeight: 600 }}>
                    WhatsApp rate-limited pairing code requests. Wait a few
                    minutes and try again, or switch to QR code login.
                  </span>
                )
                : lastStatus === "streamMode:TOS_BLOCK" ||
                    lastStatus === "streamMode:SMB_TOS_BLOCK"
                ? (
                  <span style={{ color: "#ff5555", fontWeight: 600 }}>
                    This WhatsApp account has been banned/blocked by WhatsApp
                    (TOS Block).
                  </span>
                )
                : launchFailureReason
                ? (
                  <span style={{ color: "#ff5555", fontWeight: 600 }}>
                    The worker's browser failed to start, so no QR code can be
                    generated: {launchFailureReason}
                  </span>
                )
                : "Waiting for QR code or pairing code..."}
            </div>
          )}

          {!isConnected && <ConnectHelp />}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <label style={{ fontSize: 12, opacity: 0.8 }}>
              <input
                type="checkbox"
                checked={preferCodeLogin ?? false}
                onChange={(e) =>
                  setLoginMethod(
                    phoneNumber,
                    e.currentTarget.checked,
                  )}
                style={{ marginRight: 6 }}
              />
              Prefer pairing code (instead of QR)
            </label>
            <label
              style={{ fontSize: 12, opacity: 0.5, cursor: "not-allowed" }}
            >
              <input
                type="checkbox"
                checked={false}
                disabled
                style={{ marginRight: 6 }}
              />
              Participate in warmup network (Disabled)
            </label>
          </div>
          <div
            style={accountActionRowStyle}
          >
            <Button
              className="primary"
              disabled={!isConnected}
              onClick={() => sendTest(phoneNumber, dbUser?.whatsapp)}
            >
              <PlayIcon size={12} /> Send test
            </Button>
            <Button
              className="secondary"
              disabled={isBusy || isConnected}
              onClick={() => reconnect(phoneNumber)}
              title={isConnected
                ? "Cannot generate QR or code while connected"
                : undefined}
            >
              {preferCodeLogin
                ? <MailIcon size={13} />
                : <QrCodeIcon size={13} />}
              {preferCodeLogin ? "Generate Login Code" : "Generate QR Code"}
            </Button>
            <Button
              className="secondary"
              onClick={() => changeAccountAvatar(phoneNumber)}
            >
              <ImageIcon size={13} /> Change Avatar
            </Button>
            <Button
              className="secondary"
              onClick={() => changeAccountBio(phoneNumber)}
            >
              <MessageSquareIcon size={13} /> Change Bio
            </Button>
            <Button
              className="secondary"
              onClick={() => changeAccountPrivacy(phoneNumber)}
            >
              <LockIcon size={13} /> Privacy Settings
            </Button>
            <Button
              className="secondary"
              disabled={isBusy}
              onClick={async () => {
                if (
                  !await confirmDialog(
                    "Are you sure you want to clear all credentials for this WhatsApp number? This cannot be undone.",
                    {
                      title: "Reset WhatsApp credentials",
                      okLabel: "Reset",
                      danger: true,
                    },
                  )
                ) return;
                const token = await getToken(phoneNumber);
                const promise = client({
                  endpoint: "clearCredentials",
                  payload: { phoneNumber, token },
                }).then((res) => {
                  if (!res?.success) {
                    throw new Error(
                      res?.error || "Failed to clear credentials",
                    );
                  }
                  return res;
                });
                await toast.promise(promise, {
                  loading: "Clearing credentials...",
                  success: "Credentials cleared",
                  error: (e) => e?.message || "Failed to clear credentials",
                });
              }}
            >
              <RefreshCwIcon size={13} /> Reset
            </Button>
            <Button
              className="secondary"
              style={{
                color: "var(--error)",
                borderColor: "var(--error)",
              }}
              onClick={async () => {
                if (!authUser) {
                  await alertDialog("Not authenticated", {
                    title: "Not authenticated",
                  });
                  return;
                }
                if (
                  !await confirmDialog(
                    "Are you sure you want to DELETE this account? This will stop the server and remove all data.",
                    {
                      title: "Delete WhatsApp account",
                      okLabel: "Delete",
                      danger: true,
                    },
                  )
                ) return;

                await toast.promise(
                  userClient({
                    endpoint: "deleteAccount",
                    payload: {
                      refreshToken: authUser.refresh_token,
                      phoneNumber,
                    },
                  }).then((res) => {
                    if ("error" in res) throw new Error(String(res.error));
                    return res;
                  }),
                  {
                    loading: "Deleting account...",
                    success: "Account deleted",
                    error: (e) => e.message || "Failed to delete account",
                  },
                );
              }}
            >
              <TrashIcon size={13} /> Delete
            </Button>
          </div>
        </div>
      )}
      {mismatchInfo && (
        <PhoneNumberMismatchModal
          open={showMismatchModal}
          onClose={() => setDismissedMismatchTime(mismatchInfo.timestamp)}
          registeredPhone={phoneNumber}
          scannedPhone={mismatchInfo.scannedNumber}
          name={name}
          notes={notes}
          refreshToken={authUser?.refresh_token}
        />
      )}
    </li>
  );
};

const CreateWhatsappAccountModal = (
  { open, onClose }: { open: boolean; onClose: () => void },
) => {
  const { user: authUser } = useAuth();
  const { dbUser } = useUser();
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);

  const isVerified = !!dbUser?.phoneVerifiedAt;
  const hasPaidCapacity = (dbUser?.paidCapacity || 0) > 0 ||
    (dbUser?.monthlyPayment || 0) > 0;
  const hasNoSlots = !isVerified && !hasPaidCapacity;

  const reset = () => {
    setPhone("");
    setName("");
    setNotes("");
    setErrors({});
    setSubmitting(false);
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    const phoneNumber = phone.replace(/\D/g, "");
    if (!phoneNumber) {
      nextErrors.phone = "Phone number is required";
    } else if (phoneNumber.length < 10 || phoneNumber.length > 15) {
      nextErrors.phone =
        "Phone number must be 10-15 digits in international format (e.g. 15551234567)";
    } else if (phoneNumber.startsWith("0")) {
      nextErrors.phone =
        "Please use international format without leading zero (e.g. 1...)";
    } else {
      try {
        const parsed = parsePhoneNumber("+" + phoneNumber);
        if (!parsed || !parsed.isValid()) {
          nextErrors.phone =
            `Phone number +${phoneNumber} is not a valid international number according to telecom standards`;
        }
      } catch (_e) {
        nextErrors.phone =
          `Phone number +${phoneNumber} is not a valid international number according to telecom standards`;
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0
      ? { phoneNumber, name: name.trim(), notes: notes.trim() }
      : null;
  };

  const handleSubmit = async () => {
    const valid = validate();
    if (!valid) return;
    if (!authUser?.refresh_token) {
      toast.error("Not authenticated");
      return;
    }
    setSubmitting(true);
    try {
      const res = await userClient({
        endpoint: "createAccount",
        payload: {
          refreshToken: authUser.refresh_token,
          phoneNumber: valid.phoneNumber,
          name: valid.name || undefined,
          notes: valid.notes || undefined,
        },
      });
      if ("error" in res) throw new Error(String(res.error));
      toast.success("Account added");
      reset();
      onClose();
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : `Failed to add account. Please contact support@supergreen.cc`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add WhatsApp account">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {hasNoSlots && (
          <div
            style={{
              padding: "10px 14px",
              background: "rgba(234, 179, 8, 0.12)",
              border: "1px solid rgba(234, 179, 8, 0.35)",
              borderRadius: "var(--radius)",
              fontSize: 13,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div>
              <strong>0 active slots:</strong>{" "}
              Verify your phone via SMS for 1 free slot (7 days) or subscribe
              ($10/mo) to activate your slot before connecting.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button
                className="primary"
                style={{ fontSize: 12, padding: "5px 12px" }}
                onClick={() => setVerifyModalOpen(true)}
              >
                Verify Phone (Free Trial)
              </Button>
              <Button
                className="secondary"
                style={{ fontSize: 12, padding: "5px 12px" }}
                onClick={() => {
                  if (authUser?.refresh_token) {
                    startLineCheckout(1)(authUser.refresh_token);
                  }
                }}
              >
                Subscribe ($10/mo)
              </Button>
            </div>
            <PhoneVerificationModal
              open={verifyModalOpen}
              onClose={() => setVerifyModalOpen(false)}
            />
          </div>
        )}
        <div style={formGroupStyle}>
          <label style={labelStyle}>WhatsApp phone number</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="e.g. 15551234567"
            value={phone}
            onInput={(e) => setPhone(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            Do not connect your personal number unless you know what you're
            doing — blocks may occur. AI agents and automations work best with
            dedicated WhatsApp accounts.
          </div>
          {errors.phone && <div style={errorTextStyle}>{errors.phone}</div>}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Account Name</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="e.g. Customer Support"
            value={name}
            onInput={(e) => setName(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            A friendly name to identify this account.
          </div>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Notes</label>
          <textarea
            style={{ ...inputStyle, minHeight: 60, fontFamily: "inherit" }}
            placeholder="e.g. Used for marketing campaigns"
            value={notes}
            onInput={(e) => setNotes(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            Any additional details or context for this account.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 8,
          }}
        >
          <Button
            className="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className="primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Adding..." : "Add Account"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const WhatsappAccounts = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, error, isLoading } = useQuery({
    whatsappAccounts: {
      admins: {},
      server: {},
    },
  });
  if (error) return <ErrorMessage error={error} />;
  if (isLoading) {
    return <LoadingIndication text="Loading WhatsApp accounts..." />;
  }
  const whatsappAccounts = [...data.whatsappAccounts].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "")
  );

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>WhatsApp accounts</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={() => setModalOpen(true)}
        >
          + Add
        </Button>
      </div>
      <CreateWhatsappAccountModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
      {whatsappAccounts.length === 0
        ? <div>No WhatsApp accounts yet.</div>
        : (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {whatsappAccounts.map((account) => (
              <WaAccount key={account.id} account={account} />
            ))}
          </ul>
        )}
    </div>
  );
};

const TgAccount = (
  {
    account: {
      id,
      admins,
      secretToken,
      webhook,
      webhookUrls,
      name,
      sessionString,
      statusHistory,
      phoneNumber,
      isConnected,
      isEnabled,
      lastEventTime,
    },
  }: {
    account: InstaQLEntity<
      typeof schema,
      "telegramAccounts",
      // deno-lint-ignore ban-types
      { admins: {} }
    >;
  },
) => {
  const { user: authUser } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const tgCardRef = useDeepLinkExpand(phoneNumber, setExpanded);
  const [codeInput, setCodeInput] = useState("");
  const now = useNow(5000);
  const lastStatusEntry = statusHistory?.[statusHistory.length - 1];
  const lastStatus = lastStatusEntry?.status;
  const awaitingCode = !sessionString;

  return (
    <li key={id} ref={tgCardRef} style={accountCardStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            opacity: 0.5,
          }}
        >
          {expanded
            ? <ChevronDownIcon size={13} />
            : <ChevronRightIcon size={13} />}
        </span>
        <div style={{ fontWeight: 600 }}>
          {name || formatPhone(phoneNumber)}
        </div>
        <span
          style={{
            color: getConnectionDisplay(!!isConnected, statusHistory, now)
              .color,
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {getConnectionDisplay(!!isConnected, statusHistory, now).label}
        </span>
        {awaitingCode && (
          <span
            style={{
              color: "#2196f3",
              fontWeight: 600,
              fontSize: 12,
              border: "1px solid #2196f3",
              padding: "2px 6px",
              borderRadius: "4px",
            }}
          >
            AWAITING CODE
          </span>
        )}
        {isEnabled === false && (
          <span
            style={{
              color: "#ff9800",
              fontWeight: 600,
              fontSize: 12,
              border: "1px solid #ff9800",
              padding: "2px 6px",
              borderRadius: "4px",
            }}
          >
            SUSPENDED
          </span>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 14, opacity: 0.85 }}>
            Number: {formatPhone(phoneNumber)}
          </div>
          {lastStatus && (
            <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
              Last status: {lastStatus}
            </div>
          )}
          {lastEventTime
            ? (
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                Last event: {formatDate(lastEventTime)}
              </div>
            )
            : lastStatusEntry?.timestamp
            ? (
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                Last event: {formatDate(lastStatusEntry.timestamp)}
              </div>
            )
            : null}
          <StatusHistorySection history={statusHistory} />
          {awaitingCode && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                background: "var(--surface)",
                borderRadius: "var(--radius)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                Check your Telegram app for a login code, then enter it below:
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  placeholder="Enter code"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.currentTarget.value)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "var(--radius)",
                    border: "1px solid var(--border)",
                    background: "var(--surface)",
                    color: "var(--text)",
                    fontSize: 14,
                    flex: 1,
                  }}
                />
                <Button
                  className="primary"
                  onClick={async () => {
                    if (!authUser?.refresh_token) {
                      toast.error("Not authenticated");
                      return;
                    }
                    const trimmed = codeInput.trim();
                    if (!trimmed) {
                      toast.error("Please enter a code");
                      return;
                    }
                    const promise = userClient({
                      endpoint: "submitTelegramCode",
                      payload: {
                        refreshToken: authUser.refresh_token,
                        phoneNumber,
                        code: trimmed,
                      },
                    }).then((res) => {
                      if ("error" in res) throw new Error(String(res.error));
                      return res;
                    });
                    await toast.promise(promise, {
                      loading: "Submitting code...",
                      success: "Code submitted",
                      error: (e) => e?.message || "Failed to submit code",
                    });
                    setCodeInput("");
                  }}
                >
                  Submit Code
                </Button>
              </div>
            </div>
          )}
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: "var(--radius)",
              background: "rgba(0, 0, 0, 0.2)",
              border: "1px solid var(--border)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                opacity: 0.9,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <KeyIcon size={14} /> API & Webhooks
            </div>
            {secretToken && <SecretToken token={secretToken} />}
            <TgWebhookEditor
              phoneNumber={phoneNumber}
              webhook={webhook}
              webhookUrls={webhookUrls}
            />
            <div
              style={{
                fontSize: 12,
                opacity: 0.85,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span>Admins:</span>
              {(admins && admins.length > 0)
                ? admins.map((admin) => (
                  <span style={adminChipStyle} key={admin.id}>
                    {admin.email}
                    <Button
                      className="secondary"
                      style={{ fontSize: 10, padding: "2px 6px" }}
                      onClick={() =>
                        removeAdmin(id, admin.id, admin.email)}
                    >
                      <TrashIcon size={11} /> Remove
                    </Button>
                  </span>
                ))
                : <span>None</span>}
              <Button
                className="secondary"
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={() =>
                  addAdmin(
                    id,
                    (admins || []).map((
                      a: { email: string },
                    ) => a.email),
                  )}
              >
                <PlusIcon size={11} /> Add Admin
              </Button>
            </div>
          </div>
          <div style={accountActionRowStyle}>
            <Button
              className="primary"
              onClick={() => sendTelegramTest(phoneNumber)}
            >
              <PlayIcon size={12} /> Send test
            </Button>
            <Button
              className="secondary"
              onClick={() => listTelegramGroups(phoneNumber)}
            >
              <ListIcon size={13} /> List groups
            </Button>
            <Button
              className="secondary"
              onClick={() => changeAccountName(phoneNumber)}
            >
              <PencilIcon size={13} /> Change Name
            </Button>
            <Button
              className="secondary"
              style={{ color: "var(--error)", borderColor: "var(--error)" }}
              onClick={async () => {
                if (!authUser) {
                  await alertDialog("Not authenticated", {
                    title: "Not authenticated",
                  });
                  return;
                }
                if (
                  !await confirmDialog(
                    "Are you sure you want to DELETE this Telegram account?",
                    {
                      title: "Delete Telegram account",
                      okLabel: "Delete",
                      danger: true,
                    },
                  )
                ) return;
                await toast.promise(
                  userClient({
                    endpoint: "deleteTelegramAccount",
                    payload: {
                      refreshToken: authUser.refresh_token,
                      phoneNumber,
                    },
                  }).then((res) => {
                    if ("error" in res) throw new Error(String(res.error));
                    return res;
                  }),
                  {
                    loading: "Deleting account...",
                    success: "Account deleted",
                    error: (e) => e.message || "Failed to delete account",
                  },
                );
              }}
            >
              <TrashIcon size={13} /> Delete
            </Button>
          </div>
        </div>
      )}
    </li>
  );
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  borderRadius: "var(--radius)",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text)",
  fontSize: 14,
  boxSizing: "border-box" as const,
};

const labelStyle = {
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 4,
  display: "block",
};

const helpTextStyle = {
  fontSize: 12,
  opacity: 0.7,
  marginTop: 4,
};

const errorTextStyle = {
  fontSize: 12,
  color: "#ff5555",
  marginTop: 4,
};

const formGroupStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: 2,
};

const normalizeTelegramPhone = (input: string) => {
  const digits = input.replace(/\D/g, "");
  if (digits.startsWith("0")) return digits.slice(1);
  return digits;
};

const CreateTelegramAccountModal = (
  { open, onClose }: { open: boolean; onClose: () => void },
) => {
  const { user: authUser } = useAuth();
  const [phone, setPhone] = useState("");
  const [apiId, setApiId] = useState("");
  const [apiHash, setApiHash] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setPhone("");
    setApiId("");
    setApiHash("");
    setErrors({});
    setSubmitting(false);
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    const normalizedPhone = normalizeTelegramPhone(phone);
    if (!normalizedPhone) {
      nextErrors.phone = "Phone number is required";
    } else if (normalizedPhone.length < 10 || normalizedPhone.length > 15) {
      nextErrors.phone =
        "Must be 10-15 digits in international format (e.g. 15551234567)";
    }
    const idNum = Number(apiId);
    if (!apiId.trim()) {
      nextErrors.apiId = "API ID is required";
    } else if (!idNum || isNaN(idNum)) {
      nextErrors.apiId = "API ID must be a number";
    }
    if (!apiHash.trim()) {
      nextErrors.apiHash = "API Hash is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0
      ? { phoneNumber: normalizedPhone, apiId: idNum, apiHash: apiHash.trim() }
      : null;
  };

  const handleSubmit = async () => {
    const valid = validate();
    if (!valid) return;
    if (!authUser?.refresh_token) {
      toast.error("Not authenticated");
      return;
    }
    setSubmitting(true);
    try {
      const res = await userClient({
        endpoint: "createTelegramAccount",
        payload: {
          refreshToken: authUser.refresh_token,
          phoneNumber: valid.phoneNumber,
          apiId: valid.apiId,
          apiHash: valid.apiHash,
        },
      });
      if ("error" in res) throw new Error(String(res.error));
      toast.success("Account added! Check your Telegram app for a login code.");
      reset();
      onClose();
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : "Failed to add account. Please contact support@supergreen.cc",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Telegram account">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={formGroupStyle}>
          <label style={labelStyle}>Your Telegram phone number</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="e.g. 15551234567"
            value={phone}
            onChange={(e) => setPhone(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            The phone number of your existing Telegram account, in international
            format without leading zero.
          </div>
          {errors.phone && <div style={errorTextStyle}>{errors.phone}</div>}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Telegram API ID</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="e.g. 12345678"
            value={apiId}
            onChange={(e) => setApiId(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            <ol
              style={{
                margin: "4px 0 0 16px",
                padding: 0,
                fontSize: 12,
                opacity: 0.85,
              }}
            >
              <li>
                Go to{" "}
                <a
                  href="https://my.telegram.org/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--primary)" }}
                >
                  my.telegram.org/apps
                </a>{" "}
                and log in with your Telegram account
              </li>
              <li>Fill "App title" and "Short name" (anything works)</li>
              <li>Click "Create application"</li>
              <li>
                Copy the <strong>api_id</strong> number here
              </li>
            </ol>
          </div>
          {errors.apiId && <div style={errorTextStyle}>{errors.apiId}</div>}
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Telegram API Hash</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="e.g. a1b2c3d4e5f6..."
            value={apiHash}
            onChange={(e) => setApiHash(e.currentTarget.value)}
          />
          <div style={helpTextStyle}>
            From the same page on{" "}
            <a
              href="https://my.telegram.org/apps"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--primary)" }}
            >
              my.telegram.org
            </a>
            . Copy the <strong>api_hash</strong>{" "}
            string here. Treat this like a password — never share it.
          </div>
          {errors.apiHash && <div style={errorTextStyle}>{errors.apiHash}</div>}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 8,
          }}
        >
          <Button
            className="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className="primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Adding..." : "Add Account"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const TelegramAccounts = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, error, isLoading } = useQuery({
    telegramAccounts: {
      admins: {},
    },
  });
  if (error) return <ErrorMessage error={error} />;
  if (isLoading) {
    return <LoadingIndication text="Loading Telegram accounts..." />;
  }
  const telegramAccounts = data.telegramAccounts;

  return (
    <div
      style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Telegram accounts</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={() => setModalOpen(true)}
        >
          + Add
        </Button>
      </div>
      <CreateTelegramAccountModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
      {telegramAccounts.length === 0
        ? <div>No Telegram accounts yet.</div>
        : (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {telegramAccounts.map((account) => (
              <TgAccount key={account.id} account={account} />
            ))}
          </ul>
        )}
    </div>
  );
};

const addPbxmeNumber = (refreshToken: string) => async () => {
  const loadingId = toast.loading("Loading countries...");
  let countries: Array<
    {
      countryId: string;
      iso: string;
      name: string;
      priceUsd: number;
      available: number;
    }
  >;
  try {
    const res = await userClient({
      endpoint: "listPbxmeCountries",
      payload: { refreshToken },
    });
    toast.dismiss(loadingId);
    if ("error" in res) {
      toast.error(String(res.error));
      return;
    }
    countries = res.countries;
  } catch (e) {
    toast.dismiss(loadingId);
    toast.error((e as Error)?.message || "Failed to load countries");
    return;
  }
  if (countries.length === 0) {
    await alertDialog("No SMS-capable countries are available right now.", {
      title: "Unavailable",
    });
    return;
  }
  const options = countries.map((c) => ({
    value: c.countryId,
    label: `${isoFlagEmoji(c.iso)}  ${c.name}`,
    description: "Click to check availability & pricing",
  }));
  const chosen = await selectDialog(options, {
    title: "Choose a country",
    message: "Pick a country for your new SMS-capable phone number.",
    defaultValue: countries[0]?.countryId,
    okLabel: "Check Availability",
    searchable: true,
  });
  if (!chosen) return;

  const country = countries.find((c) => c.countryId === chosen);
  const checkingId = toast.loading(
    `Checking availability for ${country?.name || "the country"}...`,
  );
  let availabilityRes;
  try {
    availabilityRes = await userClient({
      endpoint: "getPbxmeCountryAvailability",
      payload: { refreshToken, countryId: chosen },
    });
    toast.dismiss(checkingId);
    if ("error" in availabilityRes) {
      toast.error(String(availabilityRes.error));
      return;
    }
  } catch (e) {
    toast.dismiss(checkingId);
    toast.error((e as Error)?.message || "Failed to check availability");
    return;
  }

  if (availabilityRes.available === 0) {
    await alertDialog(
      `No numbers are currently available for ${
        country?.name || "this country"
      }. Please pick another country.`,
      { title: "No numbers available" },
    );
    return;
  }

  const confirmMsg = `Found ${availabilityRes.available} available number${
    availabilityRes.available === 1 ? "" : "s"
  } for ${country?.name || "this country"}.
${
    availabilityRes.sampleNumber
      ? `Sample number: +${availabilityRes.sampleNumber}`
      : ""
  }
Pricing: $${availabilityRes.priceUsd}/mo.

Add this new phone number for SMS?`;

  if (
    !await confirmDialog(confirmMsg, {
      title: "Confirm purchase",
      okLabel: "Buy number",
    })
  ) return;

  const promise = userClient({
    endpoint: "createPbxmeNumber",
    payload: { refreshToken, countryId: chosen },
  }).then((res) => {
    if ("error" in res) throw new Error(String(res.error));
    return res;
  });
  await toast.promise(promise, {
    loading: "Allocating number...",
    success: (r) => `Number +${r.phone} added`,
    error: (e) => e?.message || "Failed to add number.",
  });
};

const releasePhoneNumber =
  (refreshToken: string, phoneNumber: string) => async () => {
    if (
      !await confirmDialog(
        `Release +${phoneNumber}? This permanently releases the number.`,
        { title: "Release number", okLabel: "Release", danger: true },
      )
    ) return;
    const promise = userClient({
      endpoint: "releaseNumber",
      payload: { refreshToken, phoneNumber },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Releasing number...",
      success: "Number released",
      error: (e) => e?.message || "Failed to release number.",
    });
  };

const notAuthenticated = () => {
  toast.error("Not authenticated");
};

const fbAccountContainerStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 12,
} as const;

const manageBillingNoticeStyle = {
  fontSize: 13,
  opacity: 0.85,
  lineHeight: 1.5,
} as const;

const manageBillingListStyle = {
  margin: 0,
  paddingLeft: 18,
  fontSize: 13,
  opacity: 0.9,
  display: "flex",
  flexDirection: "column",
  gap: 2,
} as const;

const manageBillingActionsStyle = {
  display: "flex",
  gap: 8,
  justifyContent: "flex-end",
  marginTop: 16,
} as const;

type ManageBillingModalProps = {
  open: boolean;
  onClose: () => void;
  refreshToken: string | undefined;
  waCount: number;
  tgCount: number;
  fbCount: number;
  linkedInCount: number;
  numberCount: number;
};

const ManageBillingModal = (
  {
    open,
    onClose,
    refreshToken,
    waCount,
    tgCount,
    fbCount,
    linkedInCount,
    numberCount,
  }: ManageBillingModalProps,
) => {
  const proceed = async () => {
    if (!refreshToken) {
      toast.error("Not authenticated");
      return;
    }
    await openBillingPortal(refreshToken);
  };
  return (
    <Modal open={open} onClose={onClose} title="Manage subscription">
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={manageBillingNoticeStyle}>
          You'll be redirected to Stripe to edit or cancel your subscriptions.
          When your paid capacity drops below what you're currently using, the
          following services will be disabled automatically. Their credentials
          stay safe — resubscribe at any time to bring them back.
        </div>
        <div style={manageBillingNoticeStyle}>
          Currently active:
        </div>
        <ul style={manageBillingListStyle}>
          {waCount > 0 && (
            <li>{waCount} WhatsApp account{waCount === 1 ? "" : "s"}</li>
          )}
          {tgCount > 0 && (
            <li>{tgCount} Telegram account{tgCount === 1 ? "" : "s"}</li>
          )}
          {fbCount > 0 && (
            <li>{fbCount} Facebook account{fbCount === 1 ? "" : "s"}</li>
          )}
          {linkedInCount > 0 && (
            <li>
              {linkedInCount} LinkedIn account{linkedInCount === 1 ? "" : "s"}
            </li>
          )}
          {numberCount > 0 && (
            <li>
              {numberCount} phone number{numberCount === 1 ? "" : "s"}{" "}
              (released immediately if cancelled)
            </li>
          )}
          {waCount + tgCount + fbCount + linkedInCount + numberCount === 0 && (
            <li>No active services.</li>
          )}
        </ul>
        <div style={manageBillingNoticeStyle}>
          If you'd rather pick which accounts to keep, close this dialog and
          delete the ones you don't want first.
        </div>
        <div style={manageBillingActionsStyle}>
          <Button className="secondary" onClick={onClose}>Cancel</Button>
          <Button className="primary" onClick={proceed}>
            Continue to Stripe
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const AddFacebookGroupModal = ({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (groupId: string, name?: string) => Promise<void>;
}) => {
  const [groupId, setGroupId] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!groupId.trim()) return;
    setSubmitting(true);
    try {
      await onAdd(groupId.trim(), name.trim() || undefined);
      setGroupId("");
      setName("");
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Monitor a Facebook Group">
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          paddingTop: 6,
        }}
      >
        <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>
          Posts in this group will be automatically scraped and forwarded to
          your configured webhook endpoints.
        </p>
        <div>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            Facebook Group URL or ID *
          </label>
          <input
            required
            placeholder="e.g. https://facebook.com/groups/123456789"
            style={{
              width: "100%",
              fontSize: 13,
              padding: "8px 12px",
              borderRadius: "var(--radius)",
              border: "1px solid var(--border)",
              boxSizing: "border-box",
            }}
            value={groupId}
            onChange={(e) => setGroupId(e.currentTarget.value)}
          />
        </div>
        <div>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            Group Label (optional)
          </label>
          <input
            placeholder="e.g. Local Community Group"
            style={{
              width: "100%",
              fontSize: 13,
              padding: "8px 12px",
              borderRadius: "var(--radius)",
              border: "1px solid var(--border)",
              boxSizing: "border-box",
            }}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 8,
          }}
        >
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button className="primary" type="submit" disabled={submitting}>
            {submitting ? "Adding..." : "+ Add Group"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const EditWebhooksModal = ({
  open,
  onClose,
  initialUrls,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  initialUrls: string[];
  onSave: (urlsText: string) => Promise<void>;
}) => {
  const [urlsText, setUrlsText] = useState((initialUrls || []).join("\n"));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setUrlsText((initialUrls || []).join("\n"));
  }, [initialUrls]);

  const handleSave = async (e: Event) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(urlsText);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configure Webhook Notifications"
    >
      <form
        onSubmit={handleSave}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          paddingTop: 6,
        }}
      >
        <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>
          Enter destination URLs where scraped post events should be sent (one
          URL per line).
        </p>
        <div>
          <textarea
            placeholder="https://your-server.com/api/webhook"
            style={{
              width: "100%",
              minHeight: 90,
              fontSize: 13,
              padding: 10,
              borderRadius: "var(--radius)",
              border: "1px solid var(--border)",
              boxSizing: "border-box",
              fontFamily: "monospace",
            }}
            value={urlsText}
            onChange={(e) => setUrlsText(e.currentTarget.value)}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button className="primary" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Webhooks"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const runFbConnectionCheck = (
  checkConnection: (id: string) => Promise<boolean>,
  accountId: string,
  setChecking: (v: boolean) => void,
  setCheckResult: (v: boolean | null) => void,
) => {
  setChecking(true);
  setCheckResult(null);
  checkConnection(accountId)
    .then(setCheckResult)
    .catch((e) => toast.error(e?.message || "Connection check failed"))
    .finally(() => setChecking(false));
};

const FbAccountCard = ({
  account,
  index,
  activeLoginSessions,
  checkConnection,
  openFbLogin,
  finishFbLogin,
  releaseFbAccount,
  addGroup,
  removeGroup,
  saveWebhooks,
}: {
  account: {
    id: string;
    status?: string | null;
    kernelProfileName?: string | null;
    secretToken?: string | null;
    lastEventTime?: number | null;
    webhookUrls?: string[] | null;
    fbGroups?: Array<{
      id: string;
      groupId: string;
      name?: string;
      lastScrapedAt?: number;
      lastEventTime?: number;
    }>;
  };
  index: number;
  activeLoginSessions: Record<string, string>;
  checkConnection: (id: string) => Promise<boolean>;
  openFbLogin: (id: string) => Promise<void>;
  finishFbLogin: (id: string) => Promise<void>;
  releaseFbAccount: (id: string) => Promise<void>;
  addGroup: (id: string, groupId: string, name?: string) => Promise<void>;
  removeGroup: (groupId: string) => Promise<void>;
  saveWebhooks: (id: string, urls: string) => Promise<void>;
}) => {
  const [expanded, setExpanded] = useState(false);
  const fbCardRef = useDeepLinkExpand(account.id, setExpanded);
  const [groupsExpanded, setGroupsExpanded] = useState(false);
  const [addGroupModalOpen, setAddGroupModalOpen] = useState(false);
  const [webhooksModalOpen, setWebhooksModalOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<boolean | null>(null);

  const isLoginRequired = account.status === "loginRequired";
  const groupCount = account.fbGroups?.length || 0;
  const webhookCount = (account.webhookUrls || []).length;

  return (
    <li ref={fbCardRef} style={accountListCardStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
          flexWrap: "wrap",
          gap: 10,
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              opacity: 0.5,
            }}
          >
            {expanded
              ? <ChevronDownIcon size={13} />
              : <ChevronRightIcon size={13} />}
          </span>
          <strong style={{ fontSize: 14 }}>
            Facebook Account #{index + 1}
          </strong>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button
            style={{ fontSize: 12, padding: "4px 10px" }}
            disabled={checking}
            onClick={(e) => {
              e.stopPropagation();
              runFbConnectionCheck(
                checkConnection,
                account.id,
                setChecking,
                setCheckResult,
              );
            }}
          >
            {checking ? "Checking..." : "Check connection"}
          </Button>
          {checkResult !== null && (
            <span
              style={{
                color: checkResult ? "#2e7d32" : "#d32f2f",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {checkResult ? "Connected" : "Not connected"}
            </span>
          )}
          {activeLoginSessions[account.id]
            ? (
              <>
                <Button
                  className="primary"
                  style={{ fontSize: 12, padding: "4px 10px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openFbLogin(account.id);
                  }}
                >
                  Re-open Login Window
                </Button>
                <Button
                  style={{ fontSize: 12, padding: "4px 10px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    finishFbLogin(account.id);
                  }}
                >
                  Done Logging In
                </Button>
              </>
            )
            : isLoginRequired
            ? (
              <Button
                className="primary"
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  openFbLogin(account.id);
                }}
              >
                Log in to Facebook
              </Button>
            )
            : null}
        </div>
      </div>

      {expanded && (
        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {account.secretToken && (
            <div
              style={{
                background: "var(--surface)",
                padding: 12,
                borderRadius: "var(--radius)",
                border: "1px solid var(--border)",
              }}
            >
              <SecretToken token={account.secretToken} />
            </div>
          )}

          <div
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              padding: 12,
              background: "var(--surface)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                userSelect: "none",
                flexWrap: "wrap",
              }}
              onClick={() => setGroupsExpanded((v) => !v)}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  opacity: 0.5,
                }}
              >
                {groupsExpanded
                  ? <ChevronDownIcon size={12} />
                  : <ChevronRightIcon size={12} />}
              </span>
              <strong style={{ fontSize: 13 }}>
                Monitored Groups ({groupCount})
              </strong>
              <Button
                className="primary"
                style={{ fontSize: 11, padding: "3px 10px", marginLeft: 4 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setAddGroupModalOpen(true);
                }}
              >
                + Monitor Group
              </Button>
            </div>

            {groupsExpanded && (
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  Scraped posts will be delivered to your configured webhooks.
                </div>
                {groupCount === 0
                  ? (
                    <div
                      style={{
                        padding: "12px",
                        borderRadius: "var(--radius)",
                        border: "1px dashed var(--border)",
                        background: "var(--surface-alt)",
                        fontSize: 13,
                        opacity: 0.8,
                        textAlign: "center",
                      }}
                    >
                      No Facebook groups monitored yet. Click{" "}
                      <strong>"+ Monitor Group"</strong> to add one.
                    </div>
                  )
                  : (
                    (account.fbGroups || []).map((group) => (
                      <div
                        key={group.id}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid var(--border)",
                          borderRadius: "var(--radius)",
                          background: "var(--surface-alt)",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 180 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <UsersIcon size={14} />{" "}
                            {group.name || `Group ${group.groupId}`}
                          </div>
                          <div
                            style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}
                          >
                            {group.lastScrapedAt
                              ? `Last scraped: ${
                                formatDate(group.lastScrapedAt)
                              }`
                              : "Scrape pending..."}
                          </div>
                        </div>
                        <Button
                          style={{ fontSize: 11, padding: "3px 10px" }}
                          onClick={() => removeGroup(group.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))
                  )}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                Webhook Notifications
              </div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                {webhookCount === 0
                  ? "No webhook URLs configured"
                  : `${webhookCount} URL${
                    webhookCount === 1 ? "" : "s"
                  } configured`}
              </div>
            </div>
            <Button
              style={{ fontSize: 12, padding: "5px 12px" }}
              onClick={() => setWebhooksModalOpen(true)}
            >
              Configure Webhooks
            </Button>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
              paddingTop: 10,
              borderTop: "1px solid var(--border)",
            }}
          >
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              {account.lastEventTime
                ? `Last activity: ${formatDate(account.lastEventTime)}`
                : "No activity recorded yet"}
            </div>
            <Button
              style={{
                fontSize: 12,
                padding: "4px 12px",
                color: "#d32f2f",
                borderColor: "#d32f2f",
                background: "rgba(211, 47, 47, 0.05)",
                fontWeight: 600,
              }}
              onClick={(e) => {
                e.stopPropagation();
                releaseFbAccount(account.id);
              }}
            >
              Release Account
            </Button>
          </div>

          <AddFacebookGroupModal
            open={addGroupModalOpen}
            onClose={() => setAddGroupModalOpen(false)}
            onAdd={(groupId, name) => addGroup(account.id, groupId, name)}
          />

          <EditWebhooksModal
            open={webhooksModalOpen}
            onClose={() => setWebhooksModalOpen(false)}
            initialUrls={account.webhookUrls || []}
            onSave={(urlsText) => saveWebhooks(account.id, urlsText)}
          />
        </div>
      )}
    </li>
  );
};

const FacebookAccounts = () => {
  const { user: authUser } = useAuth();
  const [activeLoginSessions, setActiveLoginSessions] = useState<
    Record<string, string>
  >({});

  const { isLoading, error, data } = useQuery(
    authUser?.email
      ? {
        fbAccounts: {
          fbGroups: {},
          $: {
            where: {
              "admins.email": authUser.email,
              status: { $in: ["active", "loginRequired"] },
            },
          },
        },
      }
      : null,
  );

  if (!authUser || isLoading) return null;
  if (error) return <div>Error loading FB accounts</div>;

  const refreshToken = authUser.refresh_token;
  const fbAccounts = [...(data?.fbAccounts || [])].sort((a, b) =>
    a.id.localeCompare(b.id)
  );

  const addFbAccount_ = async () => {
    if (!refreshToken) return notAuthenticated();
    const promise = userClient({
      endpoint: "createFbAccount",
      payload: { refreshToken },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    const res = await toast.promise(promise, {
      loading: "Adding FB account...",
      success: "FB account added. Opening login window...",
      error: (e) => {
        if (e?.message?.includes("Capacity limit reached")) {
          return "Line limit reached. Click 'Log in to Facebook' on your existing account below.";
        }
        return e?.message || "Failed to add FB account.";
      },
    }).catch(() => null);

    if (res && "fbAccountId" in res && typeof res.fbAccountId === "string") {
      await openFbLogin(res.fbAccountId);
    }
  };

  const releaseFbAccount_ = async (fbAccountId: string) => {
    if (!refreshToken) return notAuthenticated();
    if (
      !await confirmDialog(
        "Are you sure you want to release this Facebook account? This will stop monitoring all associated groups and free up the line.",
        {
          title: "Release Facebook Account",
          okLabel: "Release Account",
          danger: true,
        },
      )
    ) {
      return;
    }
    const promise = userClient({
      endpoint: "releaseFbAccount",
      payload: { refreshToken, fbAccountId },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Releasing FB account...",
      success: "FB account released",
      error: (e) => e?.message || "Failed to release FB account",
    });
  };

  const addGroup = async (
    fbAccountId: string,
    groupId: string,
    name?: string,
  ) => {
    if (!refreshToken) return notAuthenticated();
    const promise = userClient({
      endpoint: "addFbGroup",
      payload: {
        refreshToken,
        fbAccountId,
        groupId,
        name,
      },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Adding group...",
      success: "Group added",
      error: (e) => e?.message || "Failed to add group",
    });
  };

  const removeGroup = async (fbGroupId: string) => {
    if (!refreshToken) return notAuthenticated();
    const promise = userClient({
      endpoint: "removeFbGroup",
      payload: { refreshToken, fbGroupId },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Removing group...",
      success: "Group removed",
      error: (e) => e?.message || "Failed to remove group",
    });
  };

  const saveWebhooks = async (fbAccountId: string, urlsText: string) => {
    if (!refreshToken) return notAuthenticated();
    const urls = urlsText.split("\n").map((u) => u.trim()).filter(Boolean);
    const promise = userClient({
      endpoint: "setFbAccountWebhooks",
      payload: { refreshToken, fbAccountId, webhookUrls: urls },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Saving webhooks...",
      success: "Webhooks saved",
      error: (e) => e?.message || "Failed to save webhooks",
    });
  };

  const openFbLogin = async (fbAccountId: string) => {
    if (!refreshToken) return notAuthenticated();
    const promise = userClient({
      endpoint: "getFbAccountLoginUrl",
      payload: { refreshToken, fbAccountId },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    const result = await toast.promise(promise, {
      loading: "Opening browser...",
      success: "Browser opened",
      error: (e) => e?.message || "Failed to open browser",
    });
    setActiveLoginSessions((prev) => ({
      ...prev,
      [fbAccountId]: result.sessionId,
    }));
    globalThis.open(result.loginUrl, "_blank");
  };

  const finishFbLogin = async (fbAccountId: string) => {
    if (!refreshToken) return notAuthenticated();
    const sessionId = activeLoginSessions[fbAccountId];
    if (!sessionId) return;
    await toast.promise(
      userClient({
        endpoint: "closeFbAccountLoginSession",
        payload: { refreshToken, sessionId },
      }).then((res) => {
        if ("error" in res) throw new Error(String(res.error));
        return res;
      }),
      {
        loading: "Saving session...",
        success: "Login saved",
        error: (e) => e?.message || "Failed to save session",
      },
    );
    setActiveLoginSessions((prev) => {
      const { [fbAccountId]: _, ...rest } = prev;
      return rest;
    });
  };

  const checkFbConnection = async (fbAccountId: string) => {
    if (!refreshToken) {
      notAuthenticated();
      return false;
    }
    const res = await userClient({
      endpoint: "checkFbConnection",
      payload: { refreshToken, fbAccountId },
    });
    if ("error" in res) throw new Error(String(res.error));
    return res.connected;
  };

  return (
    <div style={fbAccountContainerStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Facebook accounts</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={addFbAccount_}
        >
          + Add
        </Button>
      </div>
      {fbAccounts.length === 0
        ? (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              border: "1px dashed var(--border)",
              borderRadius: "var(--radius)",
              background: "var(--surface-alt)",
            }}
          >
            <p style={{ opacity: 0.8, margin: "0 0 12px 0", fontSize: 14 }}>
              No Facebook accounts yet.
            </p>
            <Button className="primary" onClick={addFbAccount_}>
              + Add
            </Button>
          </div>
        )
        : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div
              style={{
                padding: "8px 12px",
                borderRadius: "var(--radius)",
                background: "var(--surface-alt)",
                border: "1px solid var(--border)",
                fontSize: 13,
                opacity: 0.85,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <InfoIcon size={15} style={{ color: "var(--primary)" }} />
              <span>
                Click <strong>"Log in to Facebook"</strong>{" "}
                on an account below to launch the browser login session.
              </span>
            </div>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {fbAccounts.map((account, index) => (
                <FbAccountCard
                  key={account.id}
                  account={account}
                  index={index}
                  activeLoginSessions={activeLoginSessions}
                  checkConnection={checkFbConnection}
                  openFbLogin={openFbLogin}
                  finishFbLogin={finishFbLogin}
                  releaseFbAccount={releaseFbAccount_}
                  addGroup={addGroup}
                  removeGroup={removeGroup}
                  saveWebhooks={saveWebhooks}
                />
              ))}
            </ul>
          </div>
        )}
    </div>
  );
};

const RedditAccountCard = ({
  account,
  index,
  activeLoginSessions,
  openRedditLogin,
  finishRedditLogin,
  deleteRedditAccount: deleteAccount,
}: {
  account: {
    id: string;
    name?: string | null;
    status?: string | null;
    kernelProfileName?: string | null;
    secretToken?: string | null;
    isConnected?: boolean | null;
    lastEventTime?: number | null;
  };
  index: number;
  activeLoginSessions: Record<string, string>;
  openRedditLogin: (id?: string) => Promise<void>;
  finishRedditLogin: (id?: string) => Promise<void>;
  deleteRedditAccount: (id?: string) => Promise<void>;
}) => {
  const [expanded, setExpanded] = useState(false);
  const isLoginRequired = account.status === "loginRequired" ||
    account.isConnected === false;

  return (
    <li style={accountListCardStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
          flexWrap: "wrap",
          gap: 10,
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              opacity: 0.5,
            }}
          >
            {expanded
              ? <ChevronDownIcon size={13} />
              : <ChevronRightIcon size={13} />}
          </span>
          <strong style={{ fontSize: 14 }}>
            {account.name || `Reddit Account #${index + 1}`}
          </strong>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              color: isLoginRequired ? "#d32f2f" : "#2e7d32",
              fontWeight: 600,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {isLoginRequired ? "Login required" : "Connected"}
          </span>
          {activeLoginSessions[account.id]
            ? (
              <>
                <Button
                  className="primary"
                  style={{ fontSize: 12, padding: "4px 10px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openRedditLogin(account.id);
                  }}
                >
                  Re-open Login Window
                </Button>
                <Button
                  style={{ fontSize: 12, padding: "4px 10px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    finishRedditLogin(account.id);
                  }}
                >
                  Done Logging In
                </Button>
              </>
            )
            : isLoginRequired
            ? (
              <Button
                className="primary"
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  openRedditLogin(account.id);
                }}
              >
                Log in to Reddit
              </Button>
            )
            : null}
        </div>
      </div>

      {expanded && (
        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {account.secretToken && (
            <div
              style={{
                background: "var(--surface)",
                padding: 12,
                borderRadius: "var(--radius)",
                border: "1px solid var(--border)",
              }}
            >
              <SecretToken token={account.secretToken} />
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            {account.lastEventTime
              ? (
                <div style={{ fontSize: 12, opacity: 0.75 }}>
                  Last activity: {formatDate(account.lastEventTime)}
                </div>
              )
              : (
                <div style={{ fontSize: 12, opacity: 0.6 }}>
                  No activity recorded yet
                </div>
              )}
            <Button
              style={{
                fontSize: 11,
                padding: "2px 8px",
                color: "var(--error)",
              }}
              onClick={(e) => {
                e.stopPropagation();
                deleteAccount(account.id);
              }}
            >
              Delete Account
            </Button>
          </div>
        </div>
      )}
    </li>
  );
};

const LinkedInAccountCard = ({
  account,
  index,
  activeLoginSessions,
  openLinkedInLogin,
  finishLinkedInLogin,
  checkLinkedInConnection,
  releaseLinkedInAccount,
}: {
  account: {
    id: string;
    name?: string | null;
    status?: string | null;
    kernelProfileName?: string | null;
    secretToken?: string | null;
    isConnected?: boolean | null;
    lastEventTime?: number | null;
  };
  index: number;
  activeLoginSessions: Record<string, string>;
  openLinkedInLogin: (id: string) => Promise<void>;
  finishLinkedInLogin: (id: string) => Promise<void>;
  checkLinkedInConnection: (id: string) => Promise<void>;
  releaseLinkedInAccount: (id: string) => Promise<void>;
}) => {
  const [expanded, setExpanded] = useState(false);
  const isLoginRequired = account.status === "loginRequired" ||
    account.isConnected === false;

  return (
    <li style={accountListCardStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          userSelect: "none",
          flexWrap: "wrap",
          gap: 10,
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              opacity: 0.5,
            }}
          >
            {expanded
              ? <ChevronDownIcon size={13} />
              : <ChevronRightIcon size={13} />}
          </span>
          <strong style={{ fontSize: 14 }}>
            {account.name || `LinkedIn Account #${index + 1}`}
          </strong>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              color: isLoginRequired ? "#d32f2f" : "#2e7d32",
              fontWeight: 600,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {isLoginRequired ? "Login required" : "Connected"}
          </span>
          {activeLoginSessions[account.id]
            ? (
              <>
                <Button
                  className="primary"
                  style={{ fontSize: 12, padding: "4px 10px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openLinkedInLogin(account.id);
                  }}
                >
                  Re-open Login Window
                </Button>
                <Button
                  style={{ fontSize: 12, padding: "4px 10px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    finishLinkedInLogin(account.id);
                  }}
                >
                  Done Logging In
                </Button>
              </>
            )
            : isLoginRequired
            ? (
              <Button
                className="primary"
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  openLinkedInLogin(account.id);
                }}
              >
                Log in to LinkedIn
              </Button>
            )
            : (
              <Button
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  checkLinkedInConnection(account.id);
                }}
              >
                Check Connection
              </Button>
            )}
        </div>
      </div>

      {expanded && (
        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {account.secretToken && (
            <div
              style={{
                background: "var(--surface)",
                padding: 12,
                borderRadius: "var(--radius)",
                border: "1px solid var(--border)",
              }}
            >
              <SecretToken token={account.secretToken} />
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            {account.lastEventTime
              ? (
                <div style={{ fontSize: 12, opacity: 0.75 }}>
                  Last activity: {formatDate(account.lastEventTime)}
                </div>
              )
              : (
                <div style={{ fontSize: 12, opacity: 0.6 }}>
                  No activity recorded yet
                </div>
              )}
            <Button
              style={{
                fontSize: 11,
                padding: "2px 8px",
                color: "var(--error)",
              }}
              onClick={(e) => {
                e.stopPropagation();
                releaseLinkedInAccount(account.id);
              }}
            >
              Release Account
            </Button>
          </div>
        </div>
      )}
    </li>
  );
};

const LinkedInAccounts = () => {
  const { user: authUser } = useAuth();
  const [activeLoginSessions, setActiveLoginSessions] = useState<
    Record<string, string>
  >({});

  const { isLoading, error, data } = useQuery(
    authUser?.email
      ? {
        linkedInAccounts: {
          $: {
            where: {
              "admins.email": authUser.email,
              status: "active",
            },
          },
        },
      }
      : null,
  );

  if (!authUser || isLoading) return null;
  if (error) return <div>Error loading LinkedIn accounts</div>;

  const refreshToken = authUser.refresh_token;
  const linkedInAccounts = data?.linkedInAccounts || [];

  const addAccount = async () => {
    if (!refreshToken) return notAuthenticated();
    const res = await toast.promise(
      userClient({
        endpoint: "createLinkedInAccount",
        payload: { refreshToken },
      }).then((res) => {
        if ("error" in res) throw new Error(String(res.error));
        return res;
      }),
      {
        loading: "Creating LinkedIn account...",
        success: "LinkedIn account created",
        error: (e) => e?.message || "Failed to create LinkedIn account",
      },
    );
    if (
      res && "linkedInAccountId" in res &&
      typeof res.linkedInAccountId === "string"
    ) {
      await openLinkedInLogin(res.linkedInAccountId);
    }
  };

  const releaseAccount = async (linkedInAccountId: string) => {
    if (!refreshToken) return notAuthenticated();
    if (
      !await confirmDialog("Release this LinkedIn account?", {
        title: "Release LinkedIn Account",
        okLabel: "Release",
        danger: true,
      })
    ) {
      return;
    }
    const promise = userClient({
      endpoint: "releaseLinkedInAccount",
      payload: { refreshToken, linkedInAccountId },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Releasing LinkedIn account...",
      success: "LinkedIn account released",
      error: (e) => e?.message || "Failed to release LinkedIn account",
    });
  };

  const openLinkedInLogin = async (linkedInAccountId: string) => {
    if (!refreshToken) return notAuthenticated();
    const promise = userClient({
      endpoint: "getLinkedInAccountLoginUrl",
      payload: { refreshToken, linkedInAccountId },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    const result = await toast.promise(promise, {
      loading: "Opening browser...",
      success: "Browser opened. Log into LinkedIn in the browser window.",
      error: (e) => e?.message || "Failed to open browser",
    });
    if (result.sessionId) {
      setActiveLoginSessions((prev) => ({
        ...prev,
        [linkedInAccountId]: result.sessionId,
      }));
    }
    globalThis.open(result.loginUrl, "_blank");
  };

  const finishLinkedInLogin = async (linkedInAccountId: string) => {
    if (!refreshToken) return notAuthenticated();
    const sessionId = activeLoginSessions[linkedInAccountId];
    if (!sessionId) return;
    await toast.promise(
      userClient({
        endpoint: "closeLinkedInAccountLoginSession",
        payload: { refreshToken, sessionId },
      }).then((res) => {
        if ("error" in res) throw new Error(String(res.error));
        return res;
      }),
      {
        loading: "Verifying LinkedIn login...",
        success: "LinkedIn login verified & connected!",
        error: (e) => e?.message || "Failed to verify LinkedIn login",
      },
    );
    setActiveLoginSessions((prev) => {
      const { [linkedInAccountId]: _, ...rest } = prev;
      return rest;
    });
  };

  const checkConnection = async (linkedInAccountId: string) => {
    if (!refreshToken) return notAuthenticated();
    await toast.promise(
      userClient({
        endpoint: "checkLinkedInConnection",
        payload: { refreshToken, linkedInAccountId },
      }).then((res) => {
        if ("error" in res) throw new Error(String(res.error));
        if (!res.connected) {
          throw new Error("LinkedIn account is not logged in");
        }
        return res;
      }),
      {
        loading: "Checking LinkedIn connection...",
        success: "LinkedIn account is connected!",
        error: (e) => e?.message || "Connection check failed",
      },
    );
  };

  return (
    <div style={fbAccountContainerStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>LinkedIn accounts</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={addAccount}
        >
          + Add
        </Button>
      </div>
      {linkedInAccounts.length === 0
        ? (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              border: "1px dashed var(--border)",
              borderRadius: "var(--radius)",
              background: "var(--surface-alt)",
            }}
          >
            <p style={{ opacity: 0.8, margin: "0 0 12px 0", fontSize: 14 }}>
              No LinkedIn accounts yet.
            </p>
            <Button className="primary" onClick={addAccount}>
              + Add
            </Button>
          </div>
        )
        : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div
              style={{
                padding: "8px 12px",
                borderRadius: "var(--radius)",
                background: "var(--surface-alt)",
                border: "1px solid var(--border)",
                fontSize: 13,
                opacity: 0.85,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <InfoIcon size={15} style={{ color: "var(--primary)" }} />
              <span>
                Click <strong>"Log in to LinkedIn"</strong>{" "}
                on an account below to launch the browser login session.
              </span>
            </div>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {linkedInAccounts.map((account, index) => (
                <LinkedInAccountCard
                  key={account.id}
                  account={account}
                  index={index}
                  activeLoginSessions={activeLoginSessions}
                  openLinkedInLogin={openLinkedInLogin}
                  finishLinkedInLogin={finishLinkedInLogin}
                  checkLinkedInConnection={checkConnection}
                  releaseLinkedInAccount={releaseAccount}
                />
              ))}
            </ul>
          </div>
        )}
    </div>
  );
};

const RedditAccounts = () => {
  const { user: authUser } = useAuth();
  const [activeLoginSessions, setActiveLoginSessions] = useState<
    Record<string, string>
  >({});

  const { isLoading, error, data } = useQuery(
    authUser?.email
      ? {
        redditAccounts: {
          $: {
            where: {
              "admins.email": authUser.email,
            },
          },
        },
      }
      : null,
  );

  if (!authUser || isLoading) return null;
  if (error) return <div>Error loading Reddit accounts</div>;

  const refreshToken = authUser.refresh_token;
  const redditAccounts = data?.redditAccounts || [];

  const openRedditLogin = async (redditAccountId?: string) => {
    if (!refreshToken) return notAuthenticated();
    const promise = userClient({
      endpoint: "getRedditAccountLoginUrl",
      payload: { refreshToken, redditAccountId },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    const result = await toast.promise(promise, {
      loading: "Opening browser...",
      success: "Browser opened. Log into Reddit in the browser window.",
      error: (e) => e?.message || "Failed to open browser",
    });
    if (result.redditAccountId && result.sessionId) {
      setActiveLoginSessions((prev) => ({
        ...prev,
        [result.redditAccountId]: result.sessionId,
      }));
    }
    globalThis.open(result.loginUrl, "_blank");
  };

  const finishRedditLogin = async (redditAccountId?: string) => {
    if (!refreshToken) return notAuthenticated();
    const targetId = redditAccountId || redditAccounts[0]?.id;
    const sessionId = targetId ? activeLoginSessions[targetId] : undefined;
    if (!sessionId) return;
    await toast.promise(
      userClient({
        endpoint: "closeRedditAccountLoginSession",
        payload: { refreshToken, sessionId },
      }).then((res) => {
        if ("error" in res) throw new Error(String(res.error));
        return res;
      }),
      {
        loading: "Verifying Reddit login...",
        success: "Reddit login verified & connected!",
        error: (e) => e?.message || "Failed to verify Reddit login",
      },
    );
    if (targetId) {
      setActiveLoginSessions((prev) => {
        const { [targetId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const deleteRedditAccount_ = async (redditAccountId?: string) => {
    if (!refreshToken) return notAuthenticated();
    if (
      !await confirmDialog("Delete this Reddit account?", {
        title: "Delete Reddit Account",
        okLabel: "Delete",
        danger: true,
      })
    ) {
      return;
    }
    const promise = userClient({
      endpoint: "deleteRedditAccount",
      payload: { refreshToken, redditAccountId },
    }).then((res) => {
      if ("error" in res) throw new Error(String(res.error));
      return res;
    });
    await toast.promise(promise, {
      loading: "Deleting Reddit account...",
      success: "Reddit account deleted",
      error: (e) => e?.message || "Failed to delete Reddit account",
    });
  };

  return (
    <div style={fbAccountContainerStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Reddit accounts</h2>
        <Button
          className="primary"
          style={{ fontSize: 12, padding: "4px 12px" }}
          onClick={() => openRedditLogin()}
        >
          + Add
        </Button>
      </div>
      {redditAccounts.length === 0
        ? (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              border: "1px dashed var(--border)",
              borderRadius: "var(--radius)",
              background: "var(--surface-alt)",
            }}
          >
            <p style={{ opacity: 0.8, margin: "0 0 12px 0", fontSize: 14 }}>
              No Reddit accounts yet.
            </p>
            <Button className="primary" onClick={() => openRedditLogin()}>
              + Add
            </Button>
          </div>
        )
        : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div
              style={{
                padding: "8px 12px",
                borderRadius: "var(--radius)",
                background: "var(--surface-alt)",
                border: "1px solid var(--border)",
                fontSize: 13,
                opacity: 0.85,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <InfoIcon size={15} style={{ color: "var(--primary)" }} />
              <span>
                Click <strong>"Log in to Reddit"</strong>{" "}
                on an account below to launch the browser login session.
              </span>
            </div>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {redditAccounts.map((account, index) => (
                <RedditAccountCard
                  key={account.id}
                  account={account}
                  index={index}
                  activeLoginSessions={activeLoginSessions}
                  openRedditLogin={openRedditLogin}
                  finishRedditLogin={finishRedditLogin}
                  deleteRedditAccount={deleteRedditAccount_}
                />
              ))}
            </ul>
          </div>
        )}
    </div>
  );
};

const PhoneNumbers = () => {
  const { user: authUser } = useAuth();
  const { isLoading, error, data } = useQuery(
    authUser?.email
      ? {
        phoneNumbers: {
          smsMessages: {},
          $: {
            where: {
              "admins.email": authUser.email,
              status: "active",
            },
          },
        },
      }
      : null,
  );
  if (!authUser || isLoading) return null;
  if (error) return <div>Error loading numbers</div>;
  const refreshToken = authUser.refresh_token;
  return (
    <PhoneNumbersView
      phoneNumbers={data?.phoneNumbers || []}
      onAdd={refreshToken ? addPbxmeNumber(refreshToken) : notAuthenticated}
      onRelease={refreshToken
        ? (n) => releasePhoneNumber(refreshToken, n)()
        : notAuthenticated}
    />
  );
};

const adminPanelContainerStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column" as const,
  gap: 20,
};

const adminStatsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};

const adminStatCardStyle = {
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: 16,
  background: "var(--surface-alt)",
  display: "flex",
  flexDirection: "column" as const,
  gap: 6,
  textAlign: "center" as const,
};

const adminStatValueStyle = {
  fontSize: 28,
  fontWeight: 800,
  color: "var(--primary)",
};

const adminStatLabelStyle = {
  fontSize: 13,
  opacity: 0.8,
  fontWeight: 600,
};

const adminTableStyle = {
  width: "100%",
  borderCollapse: "collapse" as const,
  fontSize: 13,
  marginTop: 10,
};

const adminTableHeaderStyle = {
  background: "var(--surface-alt)",
  borderBottom: "2px solid var(--border)",
  textAlign: "left" as const,
  padding: 10,
  fontWeight: 600,
};

const adminTableCellStyle = {
  borderBottom: "1px solid var(--border)",
  padding: 10,
  verticalAlign: "middle" as const,
};

const adminSubTabBarStyle = {
  display: "flex",
  gap: 8,
  borderBottom: "1px solid var(--border)",
  paddingBottom: 8,
  marginBottom: 10,
  flexWrap: "wrap" as const,
};

const adminSubTabButtonStyle = (active: boolean) => ({
  padding: "6px 12px",
  borderRadius: "var(--radius)",
  border: "1px solid var(--border)",
  background: active ? "var(--primary)" : "var(--surface-alt)",
  color: active ? "white" : "var(--text)",
  fontSize: 13,
  fontWeight: active ? 600 : 400,
  cursor: "pointer",
});

interface AdminServer {
  id: string;
  name: string;
  ip: string;
  provider: string;
  accountsCount: number;
  capacity: number;
}

interface AdminProxyCountryStat {
  country: string;
  totalProxies: number;
  usedSlots: number;
  availableSlots: number;
  totalSlots: number;
}

interface AdminStats {
  numbersCount: number;
  connectedWhatsappCount: number;
  totalWhatsappCount: number;
  connectedTelegramCount: number;
  totalTelegramCount: number;
  totalUsersCount: number;
  serversCount: number;
  totalProxiesCount: number;
  totalProxyUsedSlots: number;
  totalProxyAvailableSlots: number;
}

interface AdminWhatsappAccount {
  phoneNumber: string;
  name: string;
  isConnected: boolean;
  isEnabled: boolean;
  admins: string[];
  billingUser: string | null;
}

interface AdminTelegramAccount {
  phoneNumber: string;
  name: string;
  isConnected: boolean;
  isEnabled: boolean;
  admins: string[];
  billingUser: string | null;
}

interface AdminPhoneNumber {
  number: string;
  status: string | null;
  createdAt: number;
  admins: string[];
  billingUser: string | null;
}

interface AdminFbAccount {
  id: string;
  status: string | null;
  admins: string[];
  billingUser: string | null;
}

interface AdminUser {
  email: string;
  lastSeen: number;
  createdAt: number;
  monthlyPayment: number | null;
  paidCapacity: number | null;
  stripeCustomerId: string | null;
}

export type AdminLinkedInAccount = {
  id: string;
  status: string | null;
  admins: string[];
  billingUser: string | null;
};

interface AdminStatsData {
  success: true;
  stats: AdminStats;
  servers: AdminServer[];
  proxyStatsByCountry: AdminProxyCountryStat[];
  whatsappAccounts: AdminWhatsappAccount[];
  telegramAccounts: AdminTelegramAccount[];
  phoneNumbers: AdminPhoneNumber[];
  fbAccounts: AdminFbAccount[];
  linkedInAccounts?: AdminLinkedInAccount[];
  users: AdminUser[];
}

const getSortableHeaderStyle = (active: boolean) => ({
  ...adminTableHeaderStyle,
  cursor: "pointer",
  userSelect: "none",
  background: active ? "var(--surface)" : "var(--surface-alt)",
});

const SortIndicator = ({
  active,
  order,
}: {
  active: boolean;
  order: "asc" | "desc";
}) => {
  if (!active) return null;
  return (
    <span
      style={{
        fontSize: 10,
        marginLeft: 4,
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      {order === "asc"
        ? <ChevronUpIcon size={11} />
        : <ChevronDownIcon size={11} />}
    </span>
  );
};

const compareAdminUsers = (
  field: keyof AdminUser | "billingStatus",
  order: "asc" | "desc",
) =>
(a: AdminUser, b: AdminUser) => {
  const getVal = (u: AdminUser) => {
    if (field === "billingStatus") {
      return (u.monthlyPayment || 0) > 0 || (u.paidCapacity || 0) > 0 ||
          !!u.stripeCustomerId
        ? 1
        : 0;
    }
    return u[field];
  };
  const valA = getVal(a);
  const valB = getVal(b);

  if (valA === null || valA === undefined) {
    if (valB === null || valB === undefined) return 0;
    return order === "asc" ? 1 : -1;
  }
  if (valB === null || valB === undefined) {
    return order === "asc" ? -1 : 1;
  }

  if (typeof valA === "string" && typeof valB === "string") {
    return order === "asc"
      ? valA.localeCompare(valB)
      : valB.localeCompare(valA);
  }
  const numA = Number(valA);
  const numB = Number(valB);
  return order === "asc" ? numA - numB : numB - numA;
};

const compareWhatsappAccounts = (
  field: keyof AdminWhatsappAccount,
  order: "asc" | "desc",
) =>
(a: AdminWhatsappAccount, b: AdminWhatsappAccount) => {
  const valA = a[field];
  const valB = b[field];

  if (typeof valA === "boolean" && typeof valB === "boolean") {
    if (valA === valB) {
      return a.phoneNumber.localeCompare(b.phoneNumber);
    }
    return order === "desc" ? (valA ? -1 : 1) : (valA ? 1 : -1);
  }

  if (valA === null || valA === undefined) {
    if (valB === null || valB === undefined) return 0;
    return order === "asc" ? 1 : -1;
  }
  if (valB === null || valB === undefined) {
    return order === "asc" ? -1 : 1;
  }

  if (typeof valA === "string" && typeof valB === "string") {
    return order === "asc"
      ? valA.localeCompare(valB)
      : valB.localeCompare(valA);
  }

  return 0;
};

export const AdminPanel = () => {
  const { user: authUser } = useAuth();
  const [data, setData] = useState<AdminStatsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [subTab, setSubTab] = useState<
    | "overview"
    | "infrastructure"
    | "whatsapp"
    | "telegram"
    | "phone"
    | "facebook"
    | "linkedin"
    | "users"
  >("overview");
  const [userSortField, setUserSortField] = useState<
    keyof AdminUser | "billingStatus" | null
  >(null);
  const [userSortOrder, setUserSortOrder] = useState<"asc" | "desc">("asc");
  const [whatsappSortField, setWhatsappSortField] = useState<
    keyof AdminWhatsappAccount | null
  >("isConnected");
  const [whatsappSortOrder, setWhatsappSortOrder] = useState<"asc" | "desc">(
    "desc",
  );

  useEffect(() => {
    if (!authUser?.refresh_token) {
      setError("Not authenticated");
      setIsLoading(false);
      return;
    }

    userClient({
      endpoint: "getAdminStats",
      payload: { refreshToken: authUser.refresh_token },
    })
      .then((res) => {
        if ("error" in res) {
          setError(String(res.error));
        } else if ("stats" in res) {
          setData(res);
        }
      })
      .catch((e: Error) => {
        setError(e?.message || "Failed to load admin stats");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [authUser?.refresh_token]);

  if (isLoading) {
    return <LoadingIndication text="Loading admin statistics..." />;
  }

  if (error) {
    return (
      <div style={{ padding: 20 }}>
        <ErrorMessage error={{ message: error }} />
      </div>
    );
  }

  if (!data || !data.success) {
    return (
      <div style={{ padding: 20 }}>
        <ErrorMessage error={{ message: "Failed to retrieve data" }} />
      </div>
    );
  }

  const {
    stats,
    servers,
    whatsappAccounts,
    telegramAccounts,
    phoneNumbers,
    fbAccounts,
    linkedInAccounts = [],
    users,
  } = data;

  const filterFn = <T,>(
    item: T,
    fields: Array<keyof T & string>,
  ) => {
    if (!searchTerm) return true;
    const lower = searchTerm.toLowerCase();
    return fields.some((f) => {
      const val = item[f];
      if (!val) return false;
      if (Array.isArray(val)) {
        return val.some((v) => String(v).toLowerCase().includes(lower));
      }
      return String(val).toLowerCase().includes(lower);
    });
  };

  const filteredWhatsapp = whatsappAccounts.filter((a) =>
    filterFn(a, ["phoneNumber", "name", "admins", "billingUser"])
  );
  const filteredTelegram = telegramAccounts.filter((a) =>
    filterFn(a, ["phoneNumber", "name", "admins", "billingUser"])
  );
  const filteredPhoneNumbers = phoneNumbers.filter((p) =>
    filterFn(p, ["number", "status", "admins", "billingUser"])
  );
  const filteredFbAccounts = fbAccounts.filter((f) =>
    filterFn(f, ["id", "status", "admins", "billingUser"])
  );
  const filteredLinkedInAccounts = (linkedInAccounts || []).filter((l) =>
    filterFn(l, ["id", "status", "admins", "billingUser"])
  );
  const filteredUsers = users.filter((u) =>
    filterFn(u, ["email", "stripeCustomerId"])
  );

  const sortedUsers = useMemo(() => {
    if (!userSortField) return filteredUsers;
    return [...filteredUsers].sort(
      compareAdminUsers(userSortField, userSortOrder),
    );
  }, [filteredUsers, userSortField, userSortOrder]);

  const handleSort = (field: keyof AdminUser | "billingStatus") => {
    if (userSortField === field) {
      setUserSortOrder(userSortOrder === "asc" ? "desc" : "asc");
    } else {
      setUserSortField(field);
      setUserSortOrder("asc");
    }
  };

  const handleWhatsappSort = (field: keyof AdminWhatsappAccount) => {
    if (whatsappSortField === field) {
      setWhatsappSortOrder(whatsappSortOrder === "asc" ? "desc" : "asc");
    } else {
      setWhatsappSortField(field);
      setWhatsappSortOrder(field === "isConnected" ? "desc" : "asc");
    }
  };

  const sortedWhatsapp = useMemo(() => {
    if (!whatsappSortField) return filteredWhatsapp;
    return [...filteredWhatsapp].sort(
      compareWhatsappAccounts(whatsappSortField, whatsappSortOrder),
    );
  }, [filteredWhatsapp, whatsappSortField, whatsappSortOrder]);

  return (
    <div style={adminPanelContainerStyle}>
      <h2 style={{ margin: 0 }}>Supergreen Admin Panel</h2>

      {/* Stats Cards */}
      <div style={adminStatsGridStyle}>
        <div style={adminStatCardStyle}>
          <span style={adminStatValueStyle}>{stats.totalUsersCount}</span>
          <span style={adminStatLabelStyle}>Total Users</span>
        </div>
        <div style={adminStatCardStyle}>
          <span style={adminStatValueStyle}>{stats.serversCount}</span>
          <span style={adminStatLabelStyle}>Servers Used</span>
        </div>
        <div style={adminStatCardStyle}>
          <span style={adminStatValueStyle}>
            {stats.connectedWhatsappCount} / {stats.totalWhatsappCount}
          </span>
          <span style={adminStatLabelStyle}>WhatsApp Connected</span>
        </div>
        <div style={adminStatCardStyle}>
          <span style={adminStatValueStyle}>
            {stats.connectedTelegramCount} / {stats.totalTelegramCount}
          </span>
          <span style={adminStatLabelStyle}>Telegram Connected</span>
        </div>
        <div style={adminStatCardStyle}>
          <span style={adminStatValueStyle}>{stats.numbersCount}</span>
          <span style={adminStatLabelStyle}>Purchased Numbers</span>
        </div>
      </div>

      {/* Sub Tabs Navigation */}
      <div style={adminSubTabBarStyle}>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "overview")}
          onClick={() => setSubTab("overview")}
        >
          Overview
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "infrastructure")}
          onClick={() => setSubTab("infrastructure")}
        >
          Servers ({servers?.length || 0})
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "users")}
          onClick={() => setSubTab("users")}
        >
          Users ({filteredUsers.length})
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "whatsapp")}
          onClick={() => setSubTab("whatsapp")}
        >
          WhatsApp ({filteredWhatsapp.length})
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "telegram")}
          onClick={() => setSubTab("telegram")}
        >
          Telegram ({filteredTelegram.length})
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "phone")}
          onClick={() => setSubTab("phone")}
        >
          Phone Numbers ({filteredPhoneNumbers.length})
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "facebook")}
          onClick={() => setSubTab("facebook")}
        >
          Facebook ({filteredFbAccounts.length})
        </button>
        <button
          type="button"
          style={adminSubTabButtonStyle(subTab === "linkedin")}
          onClick={() => setSubTab("linkedin")}
        >
          LinkedIn ({filteredLinkedInAccounts.length})
        </button>
      </div>

      {/* Master Search Bar */}
      <div style={{ display: "flex", gap: 10 }}>
        <input
          type="text"
          placeholder="Search by phone, email, name, or Stripe customer ID..."
          style={inputStyle}
          value={searchTerm}
          onInput={(e) => setSearchTerm(e.currentTarget.value)}
        />
        {searchTerm && (
          <Button
            className="secondary"
            onClick={() => setSearchTerm("")}
          >
            Clear
          </Button>
        )}
      </div>

      <div style={{ overflowX: "auto" }}>
        {(subTab === "overview" || subTab === "infrastructure") && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {subTab === "overview" && (
              <div
                style={{
                  padding: 16,
                  background: "var(--surface-alt)",
                  borderRadius: "var(--radius)",
                  border: "1px solid var(--border)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <h3 style={{ margin: 0, fontSize: 16 }}>
                  Admin Overview Guide
                </h3>
                <p
                  style={{
                    margin: 0,
                    opacity: 0.85,
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  Welcome back, Uri! This panel aggregates live statistics on
                  all connected numbers, users, associated accounts, servers,
                  and subscription statuses directly from the database.
                </p>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    fontSize: 13,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    opacity: 0.85,
                  }}
                >
                  <li>
                    <strong>Servers Tab</strong>{" "}
                    shows the active worker servers, accounts loaded, and
                    capacity.
                  </li>
                  <li>
                    <strong>Users Tab</strong>{" "}
                    shows everyone signed up on the platform, when they were
                    last seen, their monthly payment amount, paid capacities,
                    and whether they are paying or on free trial.
                  </li>
                  <li>
                    <strong>WhatsApp / Telegram / Facebook Tabs</strong>{" "}
                    list every individual communication bot registered in the
                    system, their real-time connection status, who admins them,
                    and the user billed for their server usage.
                  </li>
                  <li>
                    <strong>Phone Numbers Tab</strong>{" "}
                    details all virtual DIDs purchased through PBXME and which
                    user currently holds them.
                  </li>
                </ul>
              </div>
            )}

            {/* Servers Section */}
            <div
              style={{
                padding: 16,
                background: "var(--surface-alt)",
                borderRadius: "var(--radius)",
                border: "1px solid var(--border)",
              }}
            >
              <h3 style={{ margin: "0 0 12px 0", fontSize: 16 }}>
                Worker Servers Used ({servers?.length || 0})
              </h3>
              <table style={adminTableStyle}>
                <thead>
                  <tr>
                    <th style={adminTableHeaderStyle}>Server Name</th>
                    <th style={adminTableHeaderStyle}>IP Address</th>
                    <th style={adminTableHeaderStyle}>Provider</th>
                    <th style={adminTableHeaderStyle}>Accounts Loaded</th>
                    <th style={adminTableHeaderStyle}>Capacity</th>
                    <th style={adminTableHeaderStyle}>Load</th>
                  </tr>
                </thead>
                <tbody>
                  {(servers || []).map((server) => {
                    const capacity = server.capacity || 20;
                    const loadPercent = Math.round(
                      (server.accountsCount / capacity) * 100,
                    );
                    return (
                      <tr key={server.id}>
                        <td style={adminTableCellStyle}>
                          <strong>{server.name}</strong>
                        </td>
                        <td style={adminTableCellStyle}>{server.ip}</td>
                        <td style={adminTableCellStyle}>{server.provider}</td>
                        <td style={adminTableCellStyle}>
                          {server.accountsCount}
                        </td>
                        <td style={adminTableCellStyle}>{capacity}</td>
                        <td style={adminTableCellStyle}>{loadPercent}%</td>
                      </tr>
                    );
                  })}
                  {(!servers || servers.length === 0) && (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ ...adminTableCellStyle, textAlign: "center" }}
                      >
                        No active worker servers found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {subTab === "users" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th
                  style={getSortableHeaderStyle(userSortField === "email")}
                  onClick={() => handleSort("email")}
                >
                  User Email
                  <SortIndicator
                    active={userSortField === "email"}
                    order={userSortOrder}
                  />
                </th>
                <th
                  style={getSortableHeaderStyle(userSortField === "lastSeen")}
                  onClick={() => handleSort("lastSeen")}
                >
                  Last Seen
                  <SortIndicator
                    active={userSortField === "lastSeen"}
                    order={userSortOrder}
                  />
                </th>
                <th
                  style={getSortableHeaderStyle(userSortField === "createdAt")}
                  onClick={() => handleSort("createdAt")}
                >
                  Created At
                  <SortIndicator
                    active={userSortField === "createdAt"}
                    order={userSortOrder}
                  />
                </th>
                <th
                  style={getSortableHeaderStyle(
                    userSortField === "monthlyPayment",
                  )}
                  onClick={() => handleSort("monthlyPayment")}
                >
                  Monthly Payment
                  <SortIndicator
                    active={userSortField === "monthlyPayment"}
                    order={userSortOrder}
                  />
                </th>
                <th
                  style={getSortableHeaderStyle(
                    userSortField === "paidCapacity",
                  )}
                  onClick={() => handleSort("paidCapacity")}
                >
                  Paid Capacity
                  <SortIndicator
                    active={userSortField === "paidCapacity"}
                    order={userSortOrder}
                  />
                </th>
                <th
                  style={getSortableHeaderStyle(
                    userSortField === "stripeCustomerId",
                  )}
                  onClick={() => handleSort("stripeCustomerId")}
                >
                  Stripe Customer
                  <SortIndicator
                    active={userSortField === "stripeCustomerId"}
                    order={userSortOrder}
                  />
                </th>
                <th
                  style={getSortableHeaderStyle(
                    userSortField === "billingStatus",
                  )}
                  onClick={() => handleSort("billingStatus")}
                >
                  Billing Status
                  <SortIndicator
                    active={userSortField === "billingStatus"}
                    order={userSortOrder}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No users match your criteria.
                    </td>
                  </tr>
                )
                : (
                  sortedUsers.map((u) => {
                    const isPaying = (u.monthlyPayment || 0) > 0 ||
                      (u.paidCapacity || 0) > 0 || !!u.stripeCustomerId;
                    return (
                      <tr key={u.email}>
                        <td style={{ ...adminTableCellStyle, fontWeight: 600 }}>
                          {u.email}
                        </td>
                        <td style={adminTableCellStyle}>
                          {u.lastSeen ? formatDate(u.lastSeen) : "Never"}
                        </td>
                        <td style={adminTableCellStyle}>
                          {u.createdAt ? formatDate(u.createdAt) : "Unknown"}
                        </td>
                        <td style={adminTableCellStyle}>
                          {u.monthlyPayment !== null
                            ? `$${u.monthlyPayment}/mo`
                            : "$0"}
                        </td>
                        <td style={adminTableCellStyle}>
                          {u.paidCapacity !== null
                            ? `${u.paidCapacity} slots`
                            : "0"}
                        </td>
                        <td style={adminTableCellStyle}>
                          {u.stripeCustomerId
                            ? (
                              <code style={{ fontSize: 11 }}>
                                {u.stripeCustomerId}
                              </code>
                            )
                            : <span style={{ opacity: 0.5 }}>None</span>}
                        </td>
                        <td style={adminTableCellStyle}>
                          <span
                            style={{
                              background: isPaying
                                ? "rgba(46, 125, 50, 0.15)"
                                : "rgba(198, 40, 40, 0.1)",
                              color: isPaying ? "#2e7d32" : "#c62828",
                              padding: "3px 8px",
                              borderRadius: 4,
                              fontWeight: 600,
                              fontSize: 11,
                            }}
                          >
                            {isPaying ? "Paying User" : "Free / Trial"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
            </tbody>
          </table>
        )}

        {subTab === "whatsapp" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th
                  style={getSortableHeaderStyle(
                    whatsappSortField === "phoneNumber",
                  )}
                  onClick={() => handleWhatsappSort("phoneNumber")}
                >
                  Phone Number
                  <SortIndicator
                    active={whatsappSortField === "phoneNumber"}
                    order={whatsappSortOrder}
                  />
                </th>
                <th
                  style={getSortableHeaderStyle(whatsappSortField === "name")}
                  onClick={() => handleWhatsappSort("name")}
                >
                  Bot Name
                  <SortIndicator
                    active={whatsappSortField === "name"}
                    order={whatsappSortOrder}
                  />
                </th>
                <th style={adminTableHeaderStyle}>Admins</th>
                <th
                  style={getSortableHeaderStyle(
                    whatsappSortField === "billingUser",
                  )}
                  onClick={() => handleWhatsappSort("billingUser")}
                >
                  Billing User
                  <SortIndicator
                    active={whatsappSortField === "billingUser"}
                    order={whatsappSortOrder}
                  />
                </th>
                <th
                  style={getSortableHeaderStyle(
                    whatsappSortField === "isConnected",
                  )}
                  onClick={() => handleWhatsappSort("isConnected")}
                >
                  Connection
                  <SortIndicator
                    active={whatsappSortField === "isConnected"}
                    order={whatsappSortOrder}
                  />
                </th>
                <th
                  style={getSortableHeaderStyle(
                    whatsappSortField === "isEnabled",
                  )}
                  onClick={() => handleWhatsappSort("isEnabled")}
                >
                  Status
                  <SortIndicator
                    active={whatsappSortField === "isEnabled"}
                    order={whatsappSortOrder}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedWhatsapp.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No WhatsApp accounts found.
                    </td>
                  </tr>
                )
                : (
                  sortedWhatsapp.map((a) => (
                    <tr key={a.phoneNumber}>
                      <td style={{ ...adminTableCellStyle, fontWeight: 600 }}>
                        {a.phoneNumber}
                      </td>
                      <td style={adminTableCellStyle}>
                        {a.name || (
                          <span style={{ opacity: 0.5 }}>No Name</span>
                        )}
                      </td>
                      <td style={adminTableCellStyle}>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {a.admins.map((email: string) => (
                            <span
                              key={email}
                              style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                              }}
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={adminTableCellStyle}>
                        {a.billingUser || (
                          <span style={{ opacity: 0.5 }}>Unassigned</span>
                        )}
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            color: a.isConnected ? "#2e7d32" : "#c62828",
                            fontWeight: 600,
                          }}
                        >
                          {a.isConnected ? "Connected" : "Disconnected"}
                        </span>
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            background: a.isEnabled
                              ? "rgba(46, 125, 50, 0.15)"
                              : "rgba(245, 124, 0, 0.15)",
                            color: a.isEnabled ? "#2e7d32" : "#f57c00",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {a.isEnabled ? "Active" : "Suspended"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
        )}

        {subTab === "telegram" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th style={adminTableHeaderStyle}>Phone Number</th>
                <th style={adminTableHeaderStyle}>Bot Name</th>
                <th style={adminTableHeaderStyle}>Admins</th>
                <th style={adminTableHeaderStyle}>Billing User</th>
                <th style={adminTableHeaderStyle}>Connection</th>
                <th style={adminTableHeaderStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTelegram.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No Telegram accounts found.
                    </td>
                  </tr>
                )
                : (
                  filteredTelegram.map((a) => (
                    <tr key={a.phoneNumber}>
                      <td style={{ ...adminTableCellStyle, fontWeight: 600 }}>
                        {a.phoneNumber}
                      </td>
                      <td style={adminTableCellStyle}>
                        {a.name || (
                          <span style={{ opacity: 0.5 }}>No Name</span>
                        )}
                      </td>
                      <td style={adminTableCellStyle}>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {a.admins.map((email: string) => (
                            <span
                              key={email}
                              style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                              }}
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={adminTableCellStyle}>
                        {a.billingUser || (
                          <span style={{ opacity: 0.5 }}>Unassigned</span>
                        )}
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            color: a.isConnected ? "#2e7d32" : "#c62828",
                            fontWeight: 600,
                          }}
                        >
                          {a.isConnected ? "Connected" : "Disconnected"}
                        </span>
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            background: a.isEnabled
                              ? "rgba(46, 125, 50, 0.15)"
                              : "rgba(245, 124, 0, 0.15)",
                            color: a.isEnabled ? "#2e7d32" : "#f57c00",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {a.isEnabled ? "Active" : "Suspended"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
        )}

        {subTab === "phone" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th style={adminTableHeaderStyle}>Number</th>
                <th style={adminTableHeaderStyle}>Status</th>
                <th style={adminTableHeaderStyle}>Created At</th>
                <th style={adminTableHeaderStyle}>Admins</th>
                <th style={adminTableHeaderStyle}>Billing User</th>
              </tr>
            </thead>
            <tbody>
              {filteredPhoneNumbers.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No phone numbers found.
                    </td>
                  </tr>
                )
                : (
                  filteredPhoneNumbers.map((p) => (
                    <tr key={p.number}>
                      <td style={{ ...adminTableCellStyle, fontWeight: 600 }}>
                        {p.number}
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            background: p.status === "active"
                              ? "rgba(46, 125, 50, 0.15)"
                              : "rgba(198, 40, 40, 0.1)",
                            color: p.status === "active"
                              ? "#2e7d32"
                              : "#c62828",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {p.status || "Unknown"}
                        </span>
                      </td>
                      <td style={adminTableCellStyle}>
                        {p.createdAt ? formatDate(p.createdAt) : "Unknown"}
                      </td>
                      <td style={adminTableCellStyle}>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {p.admins.map((email: string) => (
                            <span
                              key={email}
                              style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                              }}
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={adminTableCellStyle}>
                        {p.billingUser || (
                          <span style={{ opacity: 0.5 }}>Unassigned</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
        )}

        {subTab === "facebook" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th style={adminTableHeaderStyle}>Account ID</th>
                <th style={adminTableHeaderStyle}>Status</th>
                <th style={adminTableHeaderStyle}>Admins</th>
                <th style={adminTableHeaderStyle}>Billing User</th>
              </tr>
            </thead>
            <tbody>
              {filteredFbAccounts.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No Facebook accounts found.
                    </td>
                  </tr>
                )
                : (
                  filteredFbAccounts.map((f) => (
                    <tr key={f.id}>
                      <td
                        style={{
                          ...adminTableCellStyle,
                          fontWeight: 600,
                          fontFamily: "monospace",
                        }}
                      >
                        {f.id}
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            background: f.status === "active"
                              ? "rgba(46, 125, 50, 0.15)"
                              : "rgba(198, 40, 40, 0.1)",
                            color: f.status === "active"
                              ? "#2e7d32"
                              : "#c62828",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {f.status || "Unknown"}
                        </span>
                      </td>
                      <td style={adminTableCellStyle}>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {f.admins.map((email: string) => (
                            <span
                              key={email}
                              style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                              }}
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={adminTableCellStyle}>
                        {f.billingUser || (
                          <span style={{ opacity: 0.5 }}>Unassigned</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
        )}

        {subTab === "linkedin" && (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th style={adminTableHeaderStyle}>Account ID</th>
                <th style={adminTableHeaderStyle}>Status</th>
                <th style={adminTableHeaderStyle}>Admins</th>
                <th style={adminTableHeaderStyle}>Billing User</th>
              </tr>
            </thead>
            <tbody>
              {filteredLinkedInAccounts.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        ...adminTableCellStyle,
                        textAlign: "center",
                        opacity: 0.6,
                      }}
                    >
                      No LinkedIn accounts found.
                    </td>
                  </tr>
                )
                : (
                  filteredLinkedInAccounts.map((l) => (
                    <tr key={l.id}>
                      <td
                        style={{
                          ...adminTableCellStyle,
                          fontWeight: 600,
                          fontFamily: "monospace",
                        }}
                      >
                        {l.id}
                      </td>
                      <td style={adminTableCellStyle}>
                        <span
                          style={{
                            background: l.status === "active"
                              ? "rgba(46, 125, 50, 0.15)"
                              : "rgba(198, 40, 40, 0.1)",
                            color: l.status === "active"
                              ? "#2e7d32"
                              : "#c62828",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {l.status || "Unknown"}
                        </span>
                      </td>
                      <td style={adminTableCellStyle}>
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {l.admins.map((email: string) => (
                            <span
                              key={email}
                              style={{
                                background: "var(--surface)",
                                border: "1px solid var(--border)",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                              }}
                            >
                              {email}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={adminTableCellStyle}>
                        {l.billingUser || (
                          <span style={{ opacity: 0.5 }}>Unassigned</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

type TabKey = DashboardTab;

const tabBarStyle = {
  display: "flex",
  gap: 4,
  padding: "12px 20px 0",
  borderBottom: "1px solid var(--border)",
  background: "var(--surface)",
  overflowX: "auto" as const,
  overflowY: "hidden" as const,
};

const tabButtonStyle = (active: boolean) => ({
  padding: "8px 16px",
  borderRadius: "var(--radius) var(--radius) 0 0",
  border: "1px solid var(--border)",
  borderBottom: active ? "1px solid var(--surface)" : "1px solid var(--border)",
  background: active ? "var(--surface)" : "var(--surface-alt)",
  color: active ? "var(--text)" : "var(--text-muted)",
  fontWeight: active ? 600 : 400,
  fontSize: 14,
  cursor: "pointer",
  whiteSpace: "nowrap" as const,
  marginBottom: active ? -1 : 0,
});

const tabKeys = new Set<string>(tabOrder.map(({ key }) => key));

const isTabKey = (value: string): value is TabKey => tabKeys.has(value);

type DeepLink = { tab: TabKey; account?: string };

// Deep link format: `#<tab>` or `#<tab>/<accountId>` (e.g. #whatsapp/15551234567)
const parseHash = (hash: string): DeepLink | null => {
  const [tab, account] = hash.replace(/^#/, "").split("/");
  if (!tab || !isTabKey(tab)) return null;
  return account ? { tab, account: decodeURIComponent(account) } : { tab };
};

const deepLinkHash = ({ tab, account }: DeepLink) =>
  account ? `#${tab}/${encodeURIComponent(account)}` : `#${tab}`;

const readDeepLink = (): DeepLink | null =>
  typeof globalThis.location === "undefined"
    ? null
    : parseHash(globalThis.location.hash);

const useDeepLink = (): [DeepLink, (link: DeepLink) => void] => {
  const [deepLink, setDeepLink] = useState<DeepLink>(
    () => readDeepLink() ?? { tab: "overview" },
  );
  useEffect(() => {
    const onHashChange = () => {
      const parsed = readDeepLink();
      if (parsed) setDeepLink(parsed);
    };
    globalThis.addEventListener("hashchange", onHashChange);
    return () => globalThis.removeEventListener("hashchange", onHashChange);
  }, []);
  const navigate = (link: DeepLink) => {
    setDeepLink(link);
    const url = new URL(globalThis.location.href);
    url.hash = deepLinkHash(link);
    globalThis.history.replaceState({}, "", url.toString());
  };
  return [deepLink, navigate];
};

const PhoneVerificationModal = ({
  open,
  onClose,
  onVerified,
}: {
  open: boolean;
  onClose: () => void;
  onVerified?: () => void;
}) => {
  const { user: authUser } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const refreshToken = authUser?.refresh_token;

  const handleSendCode = async () => {
    if (!refreshToken) {
      setError("Authentication token missing. Please log in again.");
      return;
    }
    if (!phoneNumber.trim()) {
      setError("Please enter your personal phone number.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await userClient({
        endpoint: "sendSmsVerification",
        payload: {
          refreshToken,
          phoneNumber: phoneNumber.trim(),
        },
      });
      if ("error" in res) {
        setError(res.error);
      } else {
        setStep("code");
        toast.success("Verification code sent via SMS!");
      }
    } catch (_err) {
      setError("Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!refreshToken) {
      setError("Authentication token missing. Please log in again.");
      return;
    }
    if (!code.trim()) {
      setError("Please enter the verification code.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await userClient({
        endpoint: "verifySmsCode",
        payload: {
          refreshToken,
          phoneNumber: phoneNumber.trim(),
          code: code.trim(),
        },
      });
      if ("error" in res) {
        setError(res.error);
      } else {
        toast.success("Phone verified! 1 free slot unlocked for 7 days.");
        onClose();
        onVerified?.();
      }
    } catch (_err) {
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Authenticate Personal Phone for Free Line"
      onClose={onClose}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ margin: 0, fontSize: 14, opacity: 0.85 }}>
          To unlock 1 free slot for 7 days, verify your personal phone number
          via SMS. Each personal phone number can only be used for one free
          trial account.
        </p>

        {error && <ErrorMessage error={{ message: error }} />}

        {step === "phone"
          ? (
            <>
              <label style={{ fontSize: 13, fontWeight: 600 }}>
                Personal Phone Number
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="tel"
                  placeholder="+15550199 or +447700900123"
                  value={phoneNumber}
                  onInput={(e) => setPhoneNumber(e.currentTarget.value)}
                  style={{
                    flexGrow: 1,
                    padding: "8px 12px",
                    borderRadius: "var(--radius)",
                    border: "1px solid var(--border)",
                    background: "var(--surface)",
                    color: "var(--text)",
                  }}
                />
                <Button
                  className="primary"
                  onClick={handleSendCode}
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send SMS"}
                </Button>
              </div>
            </>
          )
          : (
            <>
              <label style={{ fontSize: 13, fontWeight: 600 }}>
                Enter 6-Digit SMS Verification Code
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  placeholder="123456"
                  value={code}
                  onInput={(e) => setCode(e.currentTarget.value)}
                  style={{
                    flexGrow: 1,
                    padding: "8px 12px",
                    borderRadius: "var(--radius)",
                    border: "1px solid var(--border)",
                    background: "var(--surface)",
                    color: "var(--text)",
                  }}
                />
                <Button
                  className="primary"
                  onClick={handleVerifyCode}
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Verify Code"}
                </Button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setStep("phone");
                  setError(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-muted)",
                  fontSize: 12,
                  cursor: "pointer",
                  textAlign: "left",
                  padding: 0,
                  marginTop: 4,
                }}
              >
                ← Change phone number
              </button>
            </>
          )}
      </div>
    </Modal>
  );
};

const PhoneVerificationBanner = () => {
  const { dbUser } = useUser();
  const { user: authUser } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);

  if (!dbUser) return null;
  const isVerified = !!dbUser.phoneVerifiedAt;
  const hasPaidCapacity = (dbUser.paidCapacity || 0) > 0 ||
    (dbUser.monthlyPayment || 0) > 0;

  if (isVerified || hasPaidCapacity) return null;

  return (
    <>
      <div
        style={{
          padding: "12px 16px",
          background: "var(--surface-alt)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          margin: "12px 20px 0 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 14 }}>
          <strong>You currently have 0 slots.</strong>{" "}
          Verify your phone via SMS for 1 free slot (7 days), or subscribe to
          start immediately without SMS verification.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button
            className="secondary"
            style={{ padding: "6px 14px", fontSize: 13 }}
            onClick={() => setModalOpen(true)}
          >
            Verify Phone (Free Trial)
          </Button>
          <Button
            className="primary"
            style={{ padding: "6px 14px", fontSize: 13 }}
            onClick={() => {
              if (authUser?.refresh_token) {
                startLineCheckout(1)(authUser.refresh_token);
              }
            }}
          >
            Subscribe ($10/mo)
          </Button>
        </div>
      </div>
      <PhoneVerificationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

const overviewOnboardingCardStyle = {
  margin: "16px 20px 0 20px",
  padding: "16px 20px",
  background: "var(--surface-alt)",
  border: "1px solid var(--accent)",
  borderRadius: "var(--radius)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
} as const;

const OverviewOnboardingCard = ({
  onConnectWhatsapp,
}: {
  onConnectWhatsapp: () => void;
}) => {
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const data = useBillingData();
  if (!data) return null;
  const { capacity, waCount, tgCount, fbCount, linkedInCount } = data;
  const totalConnected = waCount + tgCount + fbCount + linkedInCount;
  if (totalConnected > 0) return null;

  return (
    <>
      <div style={overviewOnboardingCardStyle}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {capacity > 0
              ? "Your slot is ready — Connect your WhatsApp account"
              : "Step 1: Connect your WhatsApp account"}
          </div>
          <div style={{ fontSize: 13, opacity: 0.85 }}>
            {capacity > 0
              ? "Link your WhatsApp line via QR code to start automating with n8n or our REST API."
              : "Verify your phone for a 7-day free trial slot, then link your WhatsApp line via QR code to automate with n8n or our REST API."}
          </div>
        </div>
        <Button
          className="primary"
          style={{ padding: "8px 18px", fontSize: 13 }}
          onClick={() =>
            capacity > 0 ? onConnectWhatsapp() : setVerifyModalOpen(true)}
        >
          Connect WhatsApp Line →
        </Button>
      </div>
      <PhoneVerificationModal
        open={verifyModalOpen}
        onClose={() => setVerifyModalOpen(false)}
        onVerified={onConnectWhatsapp}
      />
    </>
  );
};

const DashboardTabs = () => {
  const [{ tab: activeTab }, navigate] = useDeepLink();
  const setActiveTab = (tab: TabKey) => navigate({ tab });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <PhoneVerificationBanner />
      <div style={tabBarStyle}>
        {tabOrder.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            style={tabButtonStyle(activeTab === key)}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div style={{ overflowY: "auto", flex: 1 }}>
        {activeTab === "overview" && (
          <>
            <OverviewOnboardingCard
              onConnectWhatsapp={() => setActiveTab("whatsapp")}
            />
            <div style={{ padding: "0 20px" }}>
              <BillingSummary />
            </div>
          </>
        )}
        {activeTab === "whatsapp" && <WhatsappAccounts />}
        {activeTab === "telegram" && <TelegramAccounts />}
        {activeTab === "facebook" && <FacebookAccounts />}
        {activeTab === "linkedin" && <LinkedInAccounts />}
        {activeTab === "reddit" && <RedditAccounts />}
        {activeTab === "phone" && <PhoneNumbers />}
        {activeTab === "billing" && <BillingTab />}
        {activeTab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
};

const BillingSummary = () => {
  const data = useBillingData();
  if (!data) return null;
  const {
    waCount,
    tgCount,
    numbers,
    fbCount,
    linkedInCount,
    spent,
    remaining,
    budget,
    capacity,
    trialCapacity,
    isTrial,
    trialEnd,
  } = data;

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        padding: 16,
        background: "var(--surface-alt)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginTop: 12,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600 }}>Billing Summary</div>
      <div style={{ fontSize: 13, opacity: 0.9 }}>
        <div>
          Budget: <strong>${budget}/mo</strong> ({capacity}{" "}
          slot{capacity === 1 ? "" : "s"})
          {" — "}
          Spent: <strong>${spent}</strong>, Remaining:{" "}
          <strong>${remaining}</strong>
        </div>
        {trialCapacity > 0 && (
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
            Trial includes {trialCapacity}{" "}
            WhatsApp, Telegram, Facebook, or LinkedIn slot. Phone numbers
            require paid budget.
          </div>
        )}
        {spent > 0 && (
          <div
            style={{
              marginTop: 4,
              display: "flex",
              flexWrap: "wrap",
              gap: "4px 12px",
            }}
          >
            {waCount > 0 && <span>WhatsApp: {waCount}</span>}
            {tgCount > 0 && <span>Telegram: {tgCount}</span>}
            {numbers.length > 0 && <span>Phone Numbers: {numbers.length}</span>}
            {fbCount > 0 && <span>Facebook: {fbCount}</span>}
            {linkedInCount > 0 && <span>LinkedIn: {linkedInCount}</span>}
          </div>
        )}
        {capacity === 0 && (
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
            {isTrial
              ? <span>Trial expires on {formatDateOnly(trialEnd)}</span>
              : <span>Free trial ended. Add budget to add services.</span>}
          </div>
        )}
      </div>
    </div>
  );
};

const SignedInHome = () => (
  <div style={wrapperStyle}>
    <Header />
    <DashboardTabs />
    <Footer />
  </div>
);

const BillingPage = () => (
  <div style={wrapperStyle}>
    <Header />
    <div style={{ overflowY: "auto", flex: 1 }}>
      <BillingTab />
    </div>
    <Footer />
  </div>
);

const DocsPage = () => (
  <div style={wrapperStyle}>
    <Header />
    <ApiDocs />
    <Footer />
  </div>
);

const pricingContainerStyle = {
  maxWidth: 900,
  margin: "0 auto",
  padding: "40px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 24,
  alignItems: "center",
  textAlign: "center",
} as const;

const pricingCardsWrapperStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 24,
  justifyContent: "center",
  width: "100%",
  marginTop: 16,
} as const;

const pricingCardStyle = {
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  padding: 32,
  background: "var(--surface-alt)",
  width: "100%",
  maxWidth: 380,
  flex: "1 1 300px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  alignItems: "center",
} as const;

const pricingCardPopularStyle = {
  ...pricingCardStyle,
  borderColor: "var(--primary)",
  boxShadow: "0 0 24px rgba(59, 130, 246, 0.2)",
} as const;

const pricingFeatureListStyle = {
  listStyle: "none",
  padding: 0,
  margin: "16px 0",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  fontSize: 14,
  textAlign: "left",
  width: "100%",
} as const;

const PricingFeatureItem = (
  { children }: { children: preact.ComponentChildren },
) => (
  <li style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <CheckIcon size={14} color="var(--primary)" strokeWidth={2.5} />
    <span>{children}</span>
  </li>
);

const pricingN8nBannerStyle = {
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  borderRadius: "var(--radius)",
  padding: "16px 24px",
  maxWidth: 700,
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 12,
  textAlign: "left",
} as const;

const PricingPage = () => (
  <div style={wrapperStyle}>
    <Header />
    <div style={pricingContainerStyle}>
      <h1 style={{ margin: 0, fontSize: "2.5em", fontWeight: 700 }}>Pricing</h1>
      <p
        style={{
          color: "var(--text-muted)",
          margin: 0,
          maxWidth: 600,
          fontSize: "1.1em",
        }}
      >
        Simple, modular pricing. Connect your communication slots and provision
        dedicated virtual numbers seamlessly.
      </p>

      <div style={pricingCardsWrapperStyle}>
        {/* Card 1: Solo Slot */}
        <div style={pricingCardStyle}>
          <div
            style={{
              fontSize: "1.25em",
              fontWeight: 600,
              color: "var(--primary)",
            }}
          >
            Solo Slot
          </div>
          <div style={{ fontSize: "3em", fontWeight: 700 }}>
            ${monthlyPaymentPerLine}
          </div>
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: "1em",
              marginTop: -8,
            }}
          >
            per active slot / month
          </div>
          <p
            style={{
              fontSize: 14,
              color: "var(--text-muted)",
              margin: "8px 0",
            }}
          >
            A flexible slot that can run any WhatsApp account, Telegram account,
            Facebook scraping session, or LinkedIn automation session.
          </p>
          <ul style={pricingFeatureListStyle}>
            <PricingFeatureItem>Free trial to get started</PricingFeatureItem>
            <PricingFeatureItem>
              WhatsApp (Web & API Integration)
            </PricingFeatureItem>
            <PricingFeatureItem>
              Telegram (MTProto Client & Webhooks)
            </PricingFeatureItem>
            <PricingFeatureItem>
              Facebook Group Scraping & Messaging
            </PricingFeatureItem>
            <PricingFeatureItem>
              LinkedIn Automation & Messaging
            </PricingFeatureItem>
            <PricingFeatureItem>
              Full REST API & raw payload Webhooks
            </PricingFeatureItem>
            <PricingFeatureItem>
              Native n8n community node support
            </PricingFeatureItem>
            <PricingFeatureItem>
              Dedicated, isolated sandbox container
            </PricingFeatureItem>
          </ul>
        </div>

        {/* Card 2: Agency 5-Pack */}
        <div style={pricingCardPopularStyle}>
          <div
            style={{
              alignSelf: "center",
              background: "var(--primary)",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: 100,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Popular with Agencies
          </div>
          <div
            style={{
              fontSize: "1.25em",
              fontWeight: 600,
              color: "var(--primary)",
            }}
          >
            Agency 5-Pack
          </div>
          <div style={{ fontSize: "3em", fontWeight: 700 }}>
            $45
          </div>
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: "1em",
              marginTop: -8,
            }}
          >
            / month ($9/line)
          </div>
          <p
            style={{
              fontSize: 14,
              color: "var(--text-muted)",
              margin: "8px 0",
            }}
          >
            For automation agencies and teams managing multiple client accounts.
          </p>
          <ul style={pricingFeatureListStyle}>
            <PricingFeatureItem>5 flexible service slots</PricingFeatureItem>
            <PricingFeatureItem>
              Multi-account routing & shared dashboard
            </PricingFeatureItem>
            <PricingFeatureItem>
              Independent webhooks per account
            </PricingFeatureItem>
            <PricingFeatureItem>
              Full WhatsApp & Telegram features
            </PricingFeatureItem>
            <PricingFeatureItem>Native n8n node integration</PricingFeatureItem>
            <PricingFeatureItem>
              Static proxy IP allocation per slot
            </PricingFeatureItem>
            <PricingFeatureItem>Priority email support</PricingFeatureItem>
          </ul>
        </div>

        {/* Card 3: Agency Scale */}
        <div style={pricingCardStyle}>
          <div
            style={{
              fontSize: "1.25em",
              fontWeight: 600,
              color: "var(--primary)",
            }}
          >
            Agency Scale
          </div>
          <div style={{ fontSize: "3em", fontWeight: 700 }}>
            $140
          </div>
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: "1em",
              marginTop: -8,
            }}
          >
            / month ($7/line)
          </div>
          <p
            style={{
              fontSize: 14,
              color: "var(--text-muted)",
              margin: "8px 0",
            }}
          >
            High-volume messaging infrastructure for lead-gen and scaling
            workflows.
          </p>
          <ul style={pricingFeatureListStyle}>
            <PricingFeatureItem>20 flexible service slots</PricingFeatureItem>
            <PricingFeatureItem>
              High-concurrency messaging queue
            </PricingFeatureItem>
            <PricingFeatureItem>
              Multi-tenant webhook dispatch
            </PricingFeatureItem>
            <PricingFeatureItem>
              Advanced proxy pool management
            </PricingFeatureItem>
            <PricingFeatureItem>
              Full WhatsApp, Telegram & Scraping
            </PricingFeatureItem>
            <PricingFeatureItem>Dedicated support channel</PricingFeatureItem>
          </ul>
        </div>

        {/* Card 4: Virtual Phone Numbers */}
        <div style={pricingCardStyle}>
          <div
            style={{
              fontSize: "1.25em",
              fontWeight: 600,
              color: "var(--primary)",
            }}
          >
            Virtual Numbers
          </div>
          <div
            style={{
              fontSize: "3em",
              fontWeight: 700,
              display: "flex",
              alignItems: "baseline",
              gap: 4,
            }}
          >
            Varies
          </div>
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: "1em",
              marginTop: -8,
            }}
          >
            starting from ~${pbxmeNumberPriceUsd} / month
          </div>
          <p
            style={{
              fontSize: 14,
              color: "var(--text-muted)",
              margin: "8px 0",
            }}
          >
            Dedicated virtual phone numbers for real-time SMS receiving, OTP
            verification, and webhook forwarding.
          </p>
          <ul style={pricingFeatureListStyle}>
            <PricingFeatureItem>
              Clean, dedicated numbers (DIDs)
            </PricingFeatureItem>
            <PricingFeatureItem>
              Setup fee & monthly cost vary by number
            </PricingFeatureItem>
            <PricingFeatureItem>
              Real-time SMS routing to Webhooks
            </PricingFeatureItem>
            <PricingFeatureItem>
              Perfect for WhatsApp/Telegram activation
            </PricingFeatureItem>
            <PricingFeatureItem>
              Direct integration with slot services
            </PricingFeatureItem>
            <PricingFeatureItem>
              Transparent pricing shown before purchase
            </PricingFeatureItem>
          </ul>
        </div>
      </div>

      <div style={pricingN8nBannerStyle}>
        <div>
          <strong
            style={{
              color: "#4ade80",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 4,
            }}
          >
            <ZapIcon size={15} /> Verified on n8n Cloud
          </strong>
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Search <code>Supergreen</code>{" "}
            directly on your n8n Cloud canvas, or install{" "}
            <code>n8n-nodes-supergreen</code> on self-hosted instances.
          </span>
        </div>
        <a
          href="https://www.npmjs.com/package/n8n-nodes-supergreen"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "var(--primary)",
            fontSize: 13,
            textDecoration: "underline",
            whiteSpace: "nowrap",
          }}
        >
          View on npm →
        </a>
      </div>

      <div
        style={{
          marginTop: 24,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          alignItems: "center",
        }}
      >
        <a
          href="/"
          class="button primary"
          style={{
            padding: "12px 40px",
            fontSize: 16,
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          Get Started
        </a>
        <p style={{ fontSize: 13, opacity: 0.8, margin: 0 }}>
          Have custom requirements or need help? Contact{" "}
          <a
            href={`mailto:${supportEmail}`}
            style={{ color: "var(--primary)", textDecoration: "underline" }}
          >
            {supportEmail}
          </a>
        </p>
      </div>
    </div>
    <Footer />
  </div>
);

const AdminPage = () => {
  const { user: authUser } = useAuth();
  if (authUser?.email !== "uri.valevski@gmail.com") {
    return <NotFound />;
  }
  return (
    <div style={wrapperStyle}>
      <Header />
      <div style={{ overflowY: "auto", flex: 1 }}>
        <AdminPanel />
      </div>
      <Footer />
    </div>
  );
};

const routes = [
  { path: "/app", component: SignedInHome },
  { path: "/billing", component: BillingPage },
  { path: "/admin", component: AdminPage },
];

const publicRoutes = [
  { path: "/", component: Login },
  { path: "/docs", component: DocsPage },
  { path: "/legal", component: LegalPage },
  { path: "/terms", component: TermsPage },
  { path: "/privacy", component: PrivacyPage },
  { path: "/refund-policy", component: RefundPolicyPage },
  { path: "/pricing", component: PricingPage },
];

const NotFound = () => <div>Page not found :(</div>;

const toastStyle = {
  background: "var(--surface-alt)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
} as const;

const UserProvider = ({ children }: { children: preact.ComponentChildren }) => {
  const { isLoading: authLoading, error: authError, user: authUser } =
    useAuth();

  const userQuery = useMemo(
    () =>
      authUser
        ? {
          users: { $: { where: { email: authUser.email ?? "" } } },
        }
        : null,
    [authUser?.email],
  );

  const { error: queryError, isLoading: queryLoading, data } = useQuery(
    userQuery,
  );

  const [created, setCreated] = useState(false);

  useEffect(() => {
    if (!authUser?.email) return;
    identifyPosthog(authUser.email);

    if (queryLoading) return;

    // If no user found and we haven't created one yet

    if ((!data?.users || data.users.length === 0) && !created) {
      console.log("Creating user for", authUser.email);

      setCreated(true); // Prevent double submission

      transact(
        tx.users[id()].update({
          lastSeen: Date.now(),

          pushSubscriptions: {},

          email: authUser.email,

          createdAt: Date.now(),
        }),
      );
    }
  }, [authUser, queryLoading, data, created]);

  const dbUser = data?.users?.[0] || null;

  useEffect(() => {
    if (dbUser) {
      transact(tx.users[dbUser.id].update({ lastSeen: Date.now() }));
    }
  }, [dbUser?.id]); // Only run when ID changes (stable)

  const { path: currentPath } = useLocation();
  const isPublicRoute = publicRoutes.some((r) => r.path === currentPath);

  const renderContent = () => {
    if (isPublicRoute) {
      return children;
    }
    if (authError) {
      return <ErrorMessage error={authError} />;
    }
    if (authLoading || (authUser && (queryLoading || !dbUser))) {
      return <LoadingIndication />;
    }
    if (!authUser) {
      return <Login />;
    }
    if (queryError) {
      return <ErrorMessage error={queryError} />;
    }
    return children;
  };

  return (
    <UserContext.Provider
      value={{ dbUser, isLoading: queryLoading, error: queryError }}
    >
      {renderContent()}
    </UserContext.Provider>
  );
};

const ClientOnlyToaster = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) return null;
  return <Toaster toastOptions={{ duration: 4000, style: toastStyle }} />;
};

const App = () => (
  <UserProvider>
    <ClientOnlyToaster />
    <DialogHost />
    <ErrorBoundary>
      <Router>
        {[...routes, ...publicRoutes].map(({ path, component }) => (
          <Route path={path} component={component} />
        ))}

        <Route default component={NotFound} />
      </Router>
    </ErrorBoundary>
  </UserProvider>
);

export const App_ = () => (
  <LocationProvider>
    <App />
  </LocationProvider>
);

hydrate(<App_ />);
